export const en = {
  site: {
    name: 'Salaamah',
  },
  errors: {
    genericTitle: 'Something went wrong',
    genericBody: 'An unexpected error occurred. Please try again.',
    networkTitle: 'Connection problem',
    networkBody: 'We could not reach the server. Check your internet connection and try again.',
    validationTitle: 'Please check your details',
    validationBody: 'Some information looks incorrect. Review the form and try again.',
    unauthorizedTitle: 'Session expired',
    unauthorizedBody: 'Please log in again to continue.',
    invalidTokenTitle: 'Link invalid or expired',
    invalidTokenBody: 'This link is no longer valid. Request a new one and try again.',
    tokenExpiredTitle: 'Session expired',
    tokenExpiredBody: 'Your session timed out. Please log in again.',
    conflictTitle: 'Conflict',
    conflictBody: 'That action conflicts with the current state. Refresh and try again.',
    notFoundTitle: 'Not found',
    notFoundBody: 'We could not find what you were looking for.',
    serverTitle: 'Server error',
    serverBody: 'Our servers had trouble processing your request. Please try again in a moment.',
  },
  common: {
    /** Shown while a lazy route chunk is loading (generic across auth + dashboard). */
    routeLoading: 'Loading…',
    searchPlaceholder: 'Search',
    profilePlaceholder: 'Your account',
    dismissToast: 'Dismiss',
    toastSuccessTitle: 'Success',
    toastErrorTitle: 'Something went wrong',
    accountMenu: 'Open account menu',
    cancel: 'Cancel',
    logout: 'Log out',
    loggingOut: 'Logging out…',
    logoutError: 'Could not log you out. Try again.',
    logoutErrorTitle: 'Log out failed',
    logoutConfirmTitle: 'Log out of Salaamah AI?',
    logoutConfirmBody:
      'You will be signed out of your dashboard and sent back to the log-in screen.',
    logoutConfirmHint:
      'You can log back in any time using your registered email and password.',
    selectPlaceholder: 'Select',
    /** Site scope notices (client 6.7): coordinators without a staff record see no site data. */
    siteScope: {
      unlinkedTitle: "Your account isn't linked to a service site yet",
      unlinkedBody:
        "You won't see participants, shifts or staff until an Organisation Admin adds your staff record with your service site.",
      adminUnlinkedTitle: (count: number) =>
        count === 1
          ? '1 coordinator cannot see any site data yet'
          : `${String(count)} coordinators cannot see any site data yet`,
      adminUnlinkedBody:
        'Add a staff record with their service site in Workforce (use the same email address) so they can see their participants, shifts and staff.',
      adminUnlinkedAction: 'Add staff record',
    },
    /** Shown when the API returns 403 for a record opened by direct link (e.g. another service site). */
    noAccess: {
      metaTitle: 'No access',
      title: "You don't have access to this record",
      body: 'It belongs to a service site outside your access. Ask your Organisation Admin if you need to work with it.',
      backToParticipants: 'Back to Patients Hub',
      backToWorkforce: 'Back to Workforce',
      backToIncidents: 'Back to Incident Hub',
    },
    dateField: {
      defaultPlaceholder: 'dd/mm/yyyy',
      previousMonthAriaLabel: 'Previous month',
      nextMonthAriaLabel: 'Next month',
      monthAriaLabel: 'Month',
      yearAriaLabel: 'Year',
      hourAriaLabel: 'Hour',
      minuteAriaLabel: 'Minute',
    },
  },
  sidebar: {
    navAriaLabel: 'Dashboard navigation',
    menuAriaLabel: 'Dashboard menu',
    menuHeading: 'MENU',
    expand: 'Expand sidebar',
    collapse: 'Collapse sidebar',
  },
  auth: {
    logoAriaLabel: 'Salaamah AI home',
    passwordShow: 'Show password',
    passwordHide: 'Hide password',
    validation: {
      emailRequired: 'Email is required',
      emailInvalid: 'Enter a valid email address',
      passwordRequired: 'Password is required',
      passwordMinLogin: 'Password must be at least 8 characters',
      fullNameRequired: 'Full name is required',
      fullNameMin: 'Name must be at least 2 characters',
      passwordMinSignup: 'Use at least 8 characters',
      passwordMix: 'Use a mix of letters, numbers, and symbols',
    },
    login: {
      metaTitle: 'Log in',
      metaDescription: 'Sign in to Salaamah to access your organisation dashboard.',
      heading: 'Welcome back to Salaamah',
      subheading: 'Log In',
      emailLabel: 'Email Address',
      emailPlaceholder: 'e.g., name@example.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      footerLink: "Don't have an account? Sign up",
      submit: 'Log In',
      genericError: 'Something went wrong. Try again.',
    },
    signUp: {
      metaTitle: 'Sign up',
      metaDescription: 'Create your Salaamah account.',
      heading: 'Welcome to Salaamah',
      subheading: 'Sign Up',
      fullNameLabel: 'Full Name',
      fullNamePlaceholder: 'e.g., John Doe',
      emailLabel: 'Email Address',
      emailPlaceholder: 'e.g., name@example.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'e.g., Minimum 8 characters',
      passwordHelper: 'Use a mix of letters, numbers, and symbols for better security.',
      footerLink: 'Already have an account? Log in',
      submit: 'Sign Up',
      genericError: 'Something went wrong. Try again.',
    },
    verifyEmail: {
      metaTitle: 'Verify email',
      metaDescription: 'Confirm your organisation email address.',
      checking: 'Checking your link…',
      confirmTitle: 'Confirm your email address',
      confirmBody:
        'Click the button below to finish activating your Salaamah organisation account.',
      confirmCta: 'Confirm my email',
      confirming: 'Verifying your email…',
      successTitle: 'Account verified',
      successBody: 'Your organisation is verified. Log in to continue.',
      alreadyVerifiedTitle: 'Email already verified',
      alreadyVerifiedBody: 'This email address is already verified. Log in to continue.',
      expiredTitle: 'Link expired',
      expiredBody:
        'This verification link has expired. Log in and request a new one from the dashboard.',
      errorTitle: 'Link invalid or expired',
      errorBody: 'Request a new verification email from the log-in screen after signing in.',
      goToLogin: 'Go to log in',
    },
    portalActivate: {
      metaTitle: 'Activate guardian portal',
      metaDescription: 'Set your password to access the guardian portal.',
      checking: 'Checking your invite…',
      title: 'Welcome to Salaamah',
      subtitleBefore: 'Please create a secure password to finish setting up your access for ',
      subtitleAfter: '.',
      passwordLabel: 'New Password',
      passwordPlaceholder: 'e.g., Minimum 8 characters',
      passwordHelper: 'Use a mix of letters, numbers, and symbols for better security.',
      confirmPasswordLabel: 'Confirm Password',
      confirmPasswordPlaceholder: 'e.g., Minimum 8 characters',
      submit: 'Save',
      footerLogin: 'Already have an account? Log in',
      passwordMismatch: 'Passwords must match',
      consumedTitle: 'Already activated',
      consumedBody: 'This invite has already been used. Log in with your email and password.',
      expiredTitle: 'Link expired',
      expiredBody: 'Ask your provider to send a new guardian portal invite.',
      invalidTitle: 'Link invalid',
      invalidBody: 'This invite link is not valid. Ask your provider for a new invite.',
      missingTitle: 'Missing invite',
      missingBody: 'Open the activation link from your email.',
      errorTitle: 'Something went wrong',
      errorBody: 'We could not activate your account. Try again or ask your provider for help.',
      goToLogin: 'Go to log in',
      activateSuccessTitle: 'Password saved',
      activateSuccessBodyGuardian:
        'Your guardian portal account is ready. Log in with your email and password.',
      activateSuccessBodyWorkforce:
        'Your account is ready. Log in with your email and password.',
      resetTitle: 'Reset your password',
      resetSubtitleBefore: 'Create a new password for your Salaamah account at ',
      resetSubtitleAfter: '.',
      resetSubmit: 'Save new password',
      resetSuccessTitle: 'Password updated',
      resetSuccessBody: 'Your new password is ready. Log in with your email and password.',
    },
    loginBannerRegistered: 'Account created. Check your email for the verification link, then log in.',
    loginBannerRegisteredTitle: 'Account Created',
  },
  workspace: {
    metaTitle: 'Dashboard',
    loading: 'Loading your dashboard…',
    notVerifiedHeadline: 'Account not verified',
    notVerifiedSub:
      'We sent a verification link to your email — open it to activate your organisation.',
    notVerifiedDetail:
      'Didn’t get the email? Resend it below, or sign out and try another address.',
    resendVerification: 'Resend verification email',
    resendSent:
      'We just sent a fresh verification link to your email. Please check your inbox (and spam folder) to activate your account.',
    resendSentTitle: 'Verification Email Sent',
    resendError: 'Could not resend the email. Try again in a moment.',
    resendErrorTitle: 'Could Not Send Email',
    welcomeDismissErrorTitle: 'Could Not Continue',
    welcomeHeadline: 'Account Verified! 🎉',
    welcomeSub: 'Your security check is complete. You’re ready to dive in.',
    welcomeContinue: 'Go to Dashboard',
    welcomeDismissError: 'Could not continue. Try again in a moment.',
    onboardingGate: {
      title: '⚠️ Action Required: Complete Organization Setup',
      body: 'Your account is currently in Limited Mode. To activate full features--including shift assignments, clinical reporting, and NDIS compliance gating--you must complete your onboarding requirements.',
      cta: 'Complete Onboarding Now',
    },
    dashboardTitle: 'Dashboard',
    dashboardSubtitle: 'Your dashboard overview will appear here as we add modules.',
    organisationSettings: {
      metaTitle: 'Organisation settings',
      title: 'Organisation settings',
      description:
        'Manage organisation-wide configuration. Service sites, profile, and branding live here.',
      navAriaLabel: 'Organisation settings sections',
      comingSoon: 'Coming soon',
      comingSoonBadge: 'Soon',
      overviewHeading: 'Settings sections',
      overviewSubheading: 'Pick an area to configure your organisation.',
      nav: {
        overview: 'Overview',
        serviceSites: 'Service sites',
        organisationProfile: 'Profile',
        branding: 'Branding',
      },
      cards: {
        serviceSitesTitle: 'Service sites',
        serviceSitesBody: 'Add locations, timezones, and archive sites when they close.',
        organisationProfileTitle: 'Profile',
        organisationProfileBody: 'Legal name, ABN, NDIS provider details, and primary sector.',
        brandingTitle: 'Branding',
        brandingBody: 'Organisation logo and brand colours for communications and the workspace.',
      },
      profilePlaceholder:
        'Organisation profile editing will be available here in a future release.',
      profile: {
        heading: 'Organisation profile',
        subheading:
          'Update how your organisation appears in Salaamah and keep legal and NDIS registration details current.',
        loading: 'Loading organisation profile…',
        loadErrorTitle: 'Could not load profile',
        loadError: 'Try again in a moment.',
        retryLoad: 'Try again',
        sectionTradingTitle: 'Trading & registration',
        sectionTradingDescription:
          'Registration number is assigned at signup and cannot be changed. Trading name is shown across the workspace.',
        sectionLegalTitle: 'Legal identity',
        sectionLegalDescription:
          'Legal entity name, ABN, NDIS provider number, and primary sector match your onboarding identity step.',
        save: 'Save profile',
        saving: 'Saving…',
        saveErrorTitle: 'Could not save profile',
        saveError: 'Check the form and try again.',
        savedTitle: 'Profile saved',
        savedBody: 'Your organisation profile has been updated.',
        registrationNumberLabel: 'Registration number',
        tradingNameLabel: 'Trading name',
        tradingNamePlaceholder: 'Name shown in the app and communications',
        viewActivity: 'Activity',
      },
      branding: {
        heading: 'Organisation branding',
        subheading:
          'Upload your logo and choose brand colours. These settings apply across the admin workspace and future communications.',
        loading: 'Loading branding…',
        loadErrorTitle: 'Could not load branding',
        loadError: 'Try again in a moment.',
        retryLoad: 'Try again',
        sectionLogoTitle: 'Logo',
        sectionLogoDescription: 'Use a square PNG, JPEG, or WebP up to 5 MB. Shown where your organisation name appears.',
        sectionColoursTitle: 'Brand colours',
        sectionColoursDescription: 'Primary colour is used for highlights; accent for headers and emphasis.',
        primaryColourLabel: 'Primary colour',
        accentColourLabel: 'Accent colour',
        logoUploadAria: 'Upload organisation logo',
        logoRemoveAria: 'Remove organisation logo',
        logoPlaceholderInitials: (name: string) => name.slice(0, 2).toUpperCase(),
        save: 'Save colours',
        saving: 'Saving…',
        saveErrorTitle: 'Could not save branding',
        saveError: 'Check the form and try again.',
        savedTitle: 'Branding saved',
        savedBody: 'Your organisation branding has been updated.',
        logoUpdatedTitle: 'Logo updated',
        logoRemovedTitle: 'Logo removed',
      },
      profileActivity: {
        title: 'Activity',
        dialogTitle: 'Profile activity',
        dialogDescription: 'Recorded changes to organisation profile, branding, and logo.',
        dialogCloseAriaLabel: 'Close activity dialog',
        dialogCloseCta: 'Close',
        description: 'Profile and branding changes (audit log).',
        loading: 'Loading activity…',
        empty: 'No recorded profile activity yet.',
        loadError: 'Could not load profile activity.',
        retry: 'Retry',
        systemActor: 'System',
        byActor: (name: string) => `By ${name}`,
        updatedTitle: 'Profile updated',
        updatedFieldChange: (label: string, from: string, to: string) =>
          `${label} changed from “${from}” to “${to}”.`,
        updatedFieldSet: (label: string, value: string) => `${label} set to “${value}”.`,
        updatedFieldCleared: (label: string, previous: string) =>
          `${label} cleared (was “${previous}”).`,
        logoUploaded: 'Organisation logo uploaded.',
        logoRemoved: 'Organisation logo removed.',
        fieldLabels: {
          trading_name: 'Trading name',
          legal_entity_name: 'Legal entity name',
          abn: 'ABN',
          ndis_provider_number: 'NDIS provider number',
          primary_sector: 'Primary sector',
          brand_primary_color: 'Primary colour',
          brand_accent_color: 'Accent colour',
          logo: 'Logo',
          site_management: 'Site management delegation',
        },
      },
      archiveBlockers: {
        heading: 'Resolve these before archiving:',
        futureShifts: (count: number) =>
          `${count} future shift${count === 1 ? '' : 's'} still scheduled at this site`,
        staff: (count: number) =>
          `${count} staff member${count === 1 ? '' : 's'} assigned to this site`,
        participants: (count: number) =>
          `${count} participant${count === 1 ? '' : 's'} with this primary care site`,
      },
    },
    serviceSitesHub: {
      metaTitle: 'Service sites',
      title: 'Service sites',
      description:
        'Add and manage the locations where your organisation delivers support. Archived sites stay on historical records.',
      addSite: 'Add site',
      saveSite: 'Save site',
      archiveSite: 'Archive site',
      archiveDialog: {
        title: (name: string) => `Archive service site: ${name}`,
        compactTitle: (name: string) => `Archive ${name}`,
        description:
          'Archiving removes this location from scheduling and new assignments. It does not delete past shifts, notes, or care records—they stay tied to this site name for audit and reporting.',
        effectsSectionTitle: 'What happens',
        actionRequiredTitle: 'Before you can archive',
        blockedIntro:
          'This site still has staff, participants, or future shifts linked to it. Salaamah never hard-deletes sites—you archive them—but linked records must be moved or cancelled first.',
        clearIntro:
          'No staff, participants, or future shifts are blocking this site. Confirm below to archive it.',
        effectNewShiftsLabel: 'New shifts',
        effectStaffLabel: 'Assignments',
        effectHistoryLabel: 'History',
        effectNewShifts: 'New shifts and roster assignments can no longer use this site.',
        effectStaff: 'Staff cannot be newly assigned to this site.',
        effectHistory:
          'Past shifts, notes, and records keep their original site. Use “Show archived sites” to view it again.',
        shiftsLabel: 'New shifts: ',
        shiftsBody: 'This site will no longer be available when scheduling new shifts or roster assignments.',
        assignmentLabel: 'Staff assignment: ',
        assignmentBody: 'Staff cannot be newly assigned to this site.',
        historyLabel: 'History: ',
        historyBody:
          'Archived sites stay on historical records. Use “Show archived sites” on this page to view them again.',
        blockersLabel: 'Before archiving: ',
        blockersBody:
          'Choose a new site for affected staff and participants, and cancel any future shifts at this location. Historical data always keeps its original site link.',
        chipFutureShifts: (count: number) =>
          `${count} future shift${count === 1 ? '' : 's'}`,
        chipStaff: (count: number) => `${count} staff`,
        chipParticipants: (count: number) => `${count} participant${count === 1 ? '' : 's'}`,
        chipCareTeams: (count: number) => `${count} care team${count === 1 ? '' : 's'}`,
        closeAriaLabel: 'Close archive dialog',
        cancelCta: 'Cancel',
        confirmCta: 'Confirm & Archive',
        confirmBusyCta: 'Archiving…',
        loadingBlockers: 'Checking linked staff, participants, and shifts…',
      },
      archiveResolve: {
        heading: 'Resolve linked records',
        reassignStaffShortLabel: 'Reassign staff to',
        reassignParticipantsShortLabel: 'Reassign participants to',
        reassignStaffLabel: (count: number) =>
          `Reassign ${count} staff member${count === 1 ? '' : 's'} to`,
        reassignParticipantsLabel: (count: number) =>
          `Reassign ${count} participant${count === 1 ? '' : 's'} primary site to`,
        staffColumnTitle: (count: number) =>
          `${count} staff member${count === 1 ? '' : 's'}`,
        participantColumnTitle: (count: number) =>
          `${count} participant${count === 1 ? '' : 's'}`,
        careTeamColumnTitle: (count: number) => `${count} care team${count === 1 ? '' : 's'}`,
        staffColumnSubtitle: 'Select where these staff should be assigned after this site is archived.',
        participantColumnSubtitle: 'Select the primary service site for their ongoing care.',
        careTeamColumnSubtitle:
          'Move these care teams to an active site. They will no longer be tied to the archived location.',
        reassignSiteFieldLabel: 'Active service site',
        staffCountHint: (count: number) => `${count} staff member${count === 1 ? '' : 's'}`,
        participantCountHint: (count: number) => `${count} participant${count === 1 ? '' : 's'}`,
        reassignSitePlaceholder: 'Select active site',
        cancelFutureShiftsLabel: (count: number) =>
          `Cancel ${count} future shift${count === 1 ? '' : 's'} at this site`,
        cancelReasonLabel: 'Cancellation reason',
        cancelReasonHelper: 'Applied to every future shift at this site.',
        defaultCancelReason: 'Service site archived',
        noOtherSites: 'Add another active service site before archiving this location.',
        andMoreStaff: (count: number) => `${count} more`,
        andMoreParticipants: (count: number) => `${count} more`,
        validation: {
          staffSiteRequired: 'Choose a site for staff',
          participantSiteRequired: 'Choose a site for participants',
          careTeamsSiteRequired: 'Choose an active site for care teams',
          cancelShiftsRequired: 'Confirm cancellation of future shifts',
          cancelReasonRequired: 'Enter a cancellation reason',
        },
      },
      editingLabel: (name: string) => `Editing: ${name}`,
      cancelEdit: 'Cancel edit',
      loadErrorTitle: 'Could not load sites',
      loadError: 'Could not load service sites. Try again in a moment.',
      listLoading: 'Loading service sites…',
      retryLoad: 'Try again',
      saveErrorTitle: 'Could not save site',
      saveError: 'Could not save this site. Check the form and try again.',
      archiveErrorTitle: 'Could not archive site',
      archiveError: 'This site could not be archived yet. Resolve linked shifts, staff, or participants first.',
      siteSavedTitle: 'Site saved',
      siteSavedBody: 'Your service site details have been saved.',
      siteArchivedTitle: 'Site archived',
      siteArchivedBody: 'This site is archived and no longer available for new shifts or assignments.',
      timezoneLabel: 'Timezone',
      timezonePlaceholder: 'Select timezone',
      statusActive: 'Active',
      statusArchived: 'Archived',
      includeArchived: 'Show archived sites',
      listHeading: 'Registered sites',
      editSite: 'Edit',
      formPanelAddTitle: 'Add a site',
      formPanelEditTitle: 'Edit site',
      openSection: 'Open',
      dialogAddTitle: 'Add service site',
      dialogEditTitle: (name: string) => `Edit ${name}`,
      dialogEditDescription: 'Update this location’s details. Changes apply to new shifts and assignments.',
      dialogAddDescription: 'Add a location where your organisation delivers support. Required fields are marked below.',
      dialogCloseAriaLabel: 'Close service site dialog',
      addSiteCta: 'Add site',
      activity: {
        title: 'Activity',
        dialogTitle: (siteName: string) => `Activity: ${siteName}`,
        dialogDescription: 'Recorded add, edit, and archive actions for this service site.',
        dialogCloseAriaLabel: 'Close activity dialog',
        dialogCloseCta: 'Close',
        viewActivity: 'Activity',
        description: 'Add, edit, and archive actions for this site (audit log).',
        loading: 'Loading activity…',
        empty: 'No recorded activity for this site yet.',
        loadError: 'Could not load activity for this site.',
        retry: 'Retry',
        systemActor: 'System',
        byActor: (name: string) => `By ${name}`,
        createdTitle: 'Site created',
        createdDetail: (name: string) => `Added “${name}” as a service site.`,
        createdDetailGeneric: 'This service site was added.',
        updatedTitle: 'Details updated',
        updatedFieldChange: (label: string, from: string, to: string) =>
          `${label} changed from “${from}” to “${to}”.`,
        updatedFieldSet: (label: string, value: string) => `${label} set to “${value}”.`,
        updatedFieldCleared: (label: string, previous: string) =>
          `${label} cleared (was “${previous}”).`,
        archivedTitle: 'Site archived',
        archivedDetail: 'Site marked as archived and removed from new scheduling.',
        resolvedBeforeArchive: (summary: string) => `Before archive: ${summary} were reassigned or resolved.`,
        blockerStaff: (count: number) => `${count} staff`,
        blockerParticipants: (count: number) => `${count} participants`,
        blockerShifts: (count: number) => `${count} future shifts`,
        cancelledFutureShifts: 'Future shifts at this site were cancelled.',
        cancelReason: (reason: string) => `Cancellation reason: ${reason}`,
      },
    },
    home: {
      metaTitle: 'Dashboard',
      loading: 'Loading your dashboard…',
      errorTitle: "We couldn't load your dashboard",
      errorBody: 'Please try refreshing the page.',
      welcomeHeadline: 'Welcome to your Salaamah AI Dashboard',
      welcomeSubline:
        'You’ve successfully added your first service site, participant, and team member, now you’re ready to start delivering care.',
      statsHeading: 'What you have so far...',
      stats: {
        sitesAdded: 'Sites Added',
        staffInvited: 'Staff Members Invited',
        patientIntakes: 'Patient Intake Created/Imported',
        ctaRegisterSite: 'Register New Site',
        ctaInviteStaff: 'Invite Staff',
        ctaNewIntake: 'New Patient Intake',
      },
      nextHeading: 'You’ve built the foundation. What’s next?',
      tasks: {
        siteCreated: 'Site created and address verified.',
        firstIntakeCreated: 'First patient intake created',
        staffInvited: 'Staff member invited and role assigned.',
        createTraining: 'Create your first Training Module',
        createTrainingCta: 'Go to Trainings Hub',
        createCareTeams: 'Create Care Teams',
        createCareTeamsCta: 'Go to Care Teams',
        missingPrefix: 'Missing: ',
        carePlanApproved: 'Care Plan approved by Guardian.',
        firstShiftPublished: 'First shift published to the roster.',
        scheduleShiftLabel: 'Schedule a Shift:',
        scheduleShiftBody: ' Connect your staff to your site and start the clock.',
        scheduleShiftCta: 'Go to Shift Management',
      },
      operational: {
        metaTitle: 'Dashboard',
        loadingLabel: 'Loading dashboard…',
        headline: 'Dashboard',
        subtitle:
          'Your daily brief: Review critical alerts, verify certifications, and manage today\'s care delivery.',
        complianceHeading: 'Compliance and Safety',
        auditReadinessTitle: 'Audit Readiness Score',
        auditReadinessTrend: (percent: number) => `+${percent}% from last month`,
        auditReadinessTrendFallback: 'Based on staff documents and training compliance',
        criticalDocumentGapsTitle: 'Critical Document Gaps',
        criticalDocumentGapsDetail: 'Action Required',
        pendingConsentsTitle: 'Pending Consents',
        pendingConsentsDetail: 'Awaiting Sign-off',
        trainingCompletionTitle: 'Training Completion',
        trainingCompletionDetail: (count: number) =>
          count === 1 ? '1 lesson in progress' : `${count} lessons in progress`,
        operationsHeading: 'Operations and Delivery',
        activeStaffTitle: 'Active Staff Count',
        activeStaffDetail: (count: number) =>
          count === 1 ? '1 onboarding this week' : `${count} onboarding this week`,
        shiftCoverageTitle: 'Shift Coverage',
        shiftCoverageDetail: (count: number) =>
          count === 1 ? '1 unassigned shift' : `${count} unassigned shifts`,
        staffUtilizationTitle: 'Staff Utilization',
        staffUtilizationDetail: (hours: number) => `Average ${hours} hrs / week`,
        clockInVarianceTitle: 'Clock-in Variance',
        clockInVarianceValue: (minutes: number) =>
          minutes > 0 ? `+${String(minutes)}m` : minutes < 0 ? `${String(minutes)}m` : '0m',
        clockInVarianceDetail: 'Average vs scheduled start this week',
        clockInVarianceUnavailable: 'No clock-ins recorded this week yet',
        goToTrainingHub: 'Go to Trainings Hub',
        goToShiftManagement: 'Go to Shift Management',
        progressTrackerTitle: 'Progress Tracker',
        trainingSnapshotHeading: 'Training Progress Snapshot',
        trainingEmptyChart: 'No learners yet',
        trainingEmptyTable: 'Assign training programs to see learner progress here.',
        trainingChartAria: (completed: number, partial: number, notStarted: number) =>
          `Training progress: ${completed} completed, ${partial} partially completed, ${notStarted} not started`,
        legendCompletedLabel: 'Completed',
        legendPartialLabel: 'Partially Completed',
        legendNotStartedLabel: 'Have Yet to Commence',
        tableLearner: 'Learner',
        tableProgress: 'Progress',
        tablePerformance: 'Performance',
        performancePassed: 'Passed',
        performanceFailed: 'Failed',
        shiftsSnapshotHeading: 'Shifts Snapshot',
        shiftStatusActive: 'Active',
        shiftStatusScheduled: 'Scheduled',
        shiftsEmptyDay: 'No shifts scheduled for this day.',
        participantAge: (years: number) => `${years} ${years === 1 ? 'year' : 'years'}`,
        clockedInAt: (time: string) => `Clocked In at ${time}`,
        previousWeek: 'Previous week',
        nextWeek: 'Next week',
      },
    },
    hubs: {
      dashboard: {
        label: 'Dashboard',
      },
      organisationSettings: {
        label: 'Settings',
      },
      participantHub: {
        label: 'Patients Hub',
        metaTitle: 'Patients Hub',
        description: 'Participant Hub module page is ready. We can now build the feature content.',
      },
      trainingHub: {
        label: 'Training Hub',
        metaTitle: 'Training Hub',
        pageTitle: 'Training Hub',
        pageSubtitle:
          'Create modules, track completion, and keep your team aligned with the latest care standards.',
        newTrainingCta: 'New Training',
        sortLabel: 'Sort By',
        sortPlaceholder: 'Sort By',
        sortOptionNewest: 'Newest first',
        sortOptionTitleAsc: 'Title (A–Z)',
        sortOptionDuration: 'Duration',
        statusLabel: 'Status',
        statusPlaceholder: 'Status',
        statusOptionAll: 'All statuses',
        statusOptionDraft: 'Draft',
        statusOptionPublished: 'Published',
        categoryLabel: 'Category',
        categoryPlaceholder: 'Category',
        categoryOptionAll: 'All categories',
        categoryOptionOnboarding: 'Onboarding',
        categoryOptionNdis: 'NDIS',
        categoryOptionHealth: 'Health & safety',
        categoryOptionSpecialized: 'Specialized care',
        searchPlaceholder: 'Search Training',
        searchAriaLabel: 'Search training modules',
        categoryLabels: {
          onboarding: 'Onboarding',
          ndis: 'NDIS',
          health: 'Health & safety',
          specialized: 'Specialized care',
        },
        cardMenuAriaLabel: (courseTitle: string) => `Open actions for ${courseTitle}`,
        cardMenu: {
          edit: 'Edit',
          publish: 'Publish',
          duplicate: 'Duplicate',
          delete: 'Delete',
          preview: 'Preview',
          addLearners: 'Add Learners',
        },
        addLearnersDialog: {
          title: (programName: string) => `${programName} - Add Learners`,
          currentLearnersLabel: 'Current Learners',
          addLearnerLabel: 'Add Learner',
          addLearnerPlaceholder: 'Type or select Staff Name',
          addLearnerAriaLabel: 'Search staff to add as learners',
          cancelCta: 'Cancel',
          confirmCta: 'Add Learners',
          confirmBusyCta: 'Adding…',
          loadingLabel: 'Loading staff…',
          loadErrorTitle: 'Could not add learners',
          loadErrorBody: 'Check your connection and try again.',
          emptyStaffTitle: 'No staff available',
          emptyStaffBody: 'Add staff in Workforce before assigning this training program.',
          noMatchesLabel: 'No matching staff',
          assignedBadge: 'Assigned',
          completedBadge: 'Completed',
          alreadyAssignedErrorTitle: 'Already assigned',
          alreadyAssignedErrorBody:
            'Selected staff are already assigned or have completed this training. Choose other staff to add.',
          closeAriaLabel: 'Close',
          removeLearnerAriaLabel: (staffName: string) => `Remove ${staffName}`,
          toggleStaffAriaLabel: (staffName: string, selected: boolean) =>
            selected ? `Remove ${staffName} from selection` : `Add ${staffName} to selection`,
          successTitle: (count: number, programName: string) =>
            `${count} learner${count === 1 ? ' has' : 's have'} been added to ${programName}`,
          successBodyBefore: 'A notification has been sent to ',
          successBodyMid: ' regarding their outstanding ',
          successBodyAfter: '. This has been logged in their compliance history.',
          successBodyStaffMany: (count: number) => `${count} staff members`,
        },
        previewPage: {
          metaTitle: 'Preview Training Program',
          breadcrumbNavLabel: 'Training preview navigation',
          breadcrumbHub: 'Training Hub',
          breadcrumbCurrent: 'Preview',
          loadErrorTitle: 'Could not load training program',
          loadErrorBody: 'Return to the Training Hub and try again.',
          loadingLabel: 'Loading preview…',
        },
        detailPage: {
          metaTitle: 'Training Program',
          breadcrumbNavLabel: 'Training program navigation',
          breadcrumbHub: 'Training Hub',
          loadingLabel: 'Loading training program…',
          loadErrorTitle: 'Could not load training program',
          loadErrorBody: 'Return to the Training Hub and try again.',
          notFoundBody: 'This training program could not be found.',
          notifyToastTitle: 'Notification queued',
          notifyEmailBody: (name: string) => `An email reminder will be sent to ${name}.`,
          reminderToast: {
            successTitle: 'Training Reminder Sent',
            successBodyBefore: 'A notification has been sent to ',
            successBodyMid: ' regarding their outstanding ',
            successBodyAfter: '. This has been logged in their compliance history.',
            successBodyStaffMany: (count: number) => `${count} staff members`,
          },
          bulkRemindersToast: {
            successTitle: 'Bulk Reminders Dispatched',
            successBodyBefore: 'Successfully notified ',
            successBodyAfter: ' staff members of expired or upcoming training requirements.',
          },
          removeLearnersErrorTitle: 'Could not remove learners',
          removeLearnersErrorBody: 'Check your connection and try again.',
          remindersErrorTitle: 'Could not send reminders',
          remindersErrorBody: 'Check your connection and try again.',
          removeLearnersDialog: {
            title: (count: number) =>
              `Remove ${count} Learner${count === 1 ? '' : 's'} from this Module?`,
            bodyIntro: (count: number, programName: string) =>
              `You are about to remove ${count} staff member${count === 1 ? '' : 's'} from the ${programName} course. Please note the impact:`,
            bulletProgressLabel: 'Progress:',
            bulletProgressText: 'Any partial progress (e.g., 60% complete) will be deleted.',
            bulletNotificationsLabel: 'Notifications:',
            bulletNotificationsText:
              "The module will disappear from the staff members' 'Required Training' list in their mobile app.",
            bulletComplianceLabel: 'Compliance:',
            bulletComplianceText:
              'This module will no longer count toward (or against) their compliance percentage.',
            cancelCta: 'Cancel',
            confirmCta: 'Confirm & Remove',
            confirmBusyCta: 'Removing…',
            successTitle: 'Learners Removed',
            successBodyBefore: 'Successfully removed ',
            successBodyMid: ' staff members from ',
            successBodyAfter: '. Their training dashboard has been updated.',
          },
          header: {
            addLearnersCta: 'Add Learners',
          },
          analytics: {
            progressTrackerTitle: 'Progress Tracker',
            progressCompleted: 'Completed',
            progressPartial: 'Partially Completed',
            progressNotStarted: 'Have Yet to Commence',
            averagePerformanceTitle: 'Average Performance',
            usersLabel: (count: number) => `${count} User${count === 1 ? '' : 's'}`,
          },
          learnerTable: {
            sortLabel: 'Sort By',
            sortPlaceholder: 'Sort By',
            sortOptionNameAsc: 'Learner (A–Z)',
            sortOptionNameDesc: 'Learner (Z–A)',
            sortOptionProgressDesc: 'Progress (high to low)',
            sortOptionProgressAsc: 'Progress (low to high)',
            progressLabel: 'Progress',
            progressPlaceholder: 'Progress',
            progressOptionAll: 'All progress',
            progressOptionCompleted: 'Completed',
            progressOptionPartial: 'Partially completed',
            progressOptionNotStarted: 'Not started',
            performanceLabel: 'Performance',
            performancePlaceholder: 'Performance',
            performanceOptionAll: 'All performance',
            performanceOptionPassed: 'Passed',
            performanceOptionPending: 'Pending',
            searchPlaceholder: 'Search Learner',
            searchAriaLabel: 'Search learners',
            removeSelectedLearners: 'Remove Selected Learners?',
            sendBulkReminders: 'Send Bulk Training Reminders',
            columns: {
              learner: 'Learner',
              progress: 'Progress',
              performance: 'Performance',
              notifyVia: 'Notify via',
            },
            passedBadge: 'Passed',
            performancePending: '—',
            notifyEmail: 'Email',
            emptyLearners: 'No learners match your filters yet.',
            selectAllAriaLabel: 'Select all removable learners',
            selectLearnerAriaLabel: (name: string) => `Select ${name}`,
            selectCompletedLearnerAriaLabel: (name: string) =>
              `${name} (completed training cannot be removed)`,
            notifyCompletedLearnerAriaLabel: (name: string) =>
              `${name} (completed training cannot receive reminders)`,
          },
        },
        cardMenuPublishSuccessTitle: 'Training Program Published',
        cardMenuPublishSuccessBody: (programName: string) =>
          `${programName} is now live. You can now assign this module to Care Teams and Staff.`,
        publishProgramDialog: {
          title: 'Publish Training Program?',
          bodyBefore: 'You are about to make ',
          bodyAfter: ' active.',
          bulletMobile: 'It will appear in the Staff Mobile App for assigned users.',
          bulletCarePlans:
            'It can now be used as a mandatory requirement in Participant Care Plans.',
          keepDraftCta: 'Keep as Draft',
          publishCta: 'Publish',
          publishBusyCta: 'Publishing…',
        },
        cardMenuDuplicateSuccessTitle: 'Training duplicated',
        cardMenuDuplicateSuccessBody: 'A new draft copy was created.',
        cardMenuDeleteSuccessTitle: 'Training Program Deleted',
        cardMenuDeleteSuccessBody: (programName: string) =>
          `${programName} has been removed. Compliance records for associated staff have been updated.`,
        cardMenuActionErrorTitle: 'Could not complete action',
        cardMenuActionErrorBody: 'Please try again in a moment.',
        cardMenuPublishValidationBody:
          'Complete all required modules and knowledge-check fields before publishing.',
        cardMenuPublishValidationLabels: {
          programDetails: 'Program details',
          module: (moduleNumber: number) => `Module ${moduleNumber}`,
          knowledgeCheck: 'Knowledge check',
          modules: 'Modules',
          andMore: (count: number) => `…and ${count} more`,
        },
        deleteProgramDialog: {
          title: 'Delete Training Program?',
          bodyBefore: 'You are about to permanently delete the ',
          bodyAfter: ' training program.',
          warning:
            "This will remove all completion records for staff currently assigned to this module. If this training is a mandatory requirement for any Care Plans, those staff members will immediately show as 'Non-Compliant.'",
          cancelCta: 'Cancel',
          confirmCta: 'Confirm & Remove',
          confirmBusyCta: 'Removing…',
        },
        loadListErrorBody: 'We could not load training programs. Please try again.',
        loadingPrograms: 'Loading training programs…',
        loadingMorePrograms: 'Loading more training programs…',
        emptyStateTitle: 'No Training Program Established',
        emptyStateBody:
          "You haven't created any training program or learning paths yet. A Training Program allows you to define mandatory safety courses, clinical knowledge checks, and induction protocols for your workforce.",
        emptyFilteredTitle: 'No Training Programs Found',
        emptyFilteredBody:
          'No training programs match your filters. Try adjusting search or filters.',
        clearFiltersCta: 'Clear filters',
        emptyListBody: 'No training programs yet. Create your first course to get started.',
        demoCourses: [
          {
            id: 'welcome',
            category: 'onboarding',
            title: 'Welcome to Salaamah — Team essentials',
            description:
              'Orientation covering values, communication expectations, and how to use the workspace day to day.',
            durationMin: 35,
            dateDisplay: 'February 2, 2026',
            participants: 24,
          },
          {
            id: 'welcome2',
            category: 'onboarding',
            title: 'Welcome to Salaamah — Team essentials',
            description:
              'Orientation covering values, communication expectations, and how to use the workspace day to day.',
            durationMin: 35,
            dateDisplay: 'February 2, 2026',
            participants: 24,
          },
          {
            id: 'welcome3',
            category: 'onboarding',
            title: 'Welcome to Salaamah — Team essentials',
            description:
              'Orientation covering values, communication expectations, and how to use the workspace day to day.',
            durationMin: 35,
            dateDisplay: 'February 2, 2026',
            participants: 24,
          },
          {
            id: 'ndisRights',
            category: 'ndis',
            badgeLabel: 'NDIS Mandatory and Foundation',
            title: 'NDIS Induction',
            description:
              'A deep dive into the disability sector, including "Dignity of Risk," "Know the Person," and "Managing Challenging Situations."',
            durationMin: 60,
            dateDisplay: 'January 31, 2026',
            participants: 22,
          },
          {
            id: 'manualHandling',
            category: 'health',
            title: 'Manual handling & mobility support',
            description:
              'Safe movement techniques, equipment checks, and when to escalate for allied health input.',
            durationMin: 45,
            dateDisplay: 'January 5, 2026',
            participants: 18,
          },
          {
            id: 'manualHandling1',
            category: 'health',
            title: 'Manual handling & mobility support',
            description:
              'Safe movement techniques, equipment checks, and when to escalate for allied health input.',
            durationMin: 45,
            dateDisplay: 'January 5, 2026',
            participants: 18,
          },
          {
            id: 'infectionControl',
            category: 'health',
            title: 'Infection prevention & PPE fundamentals',
            description:
              'Standard precautions, hand hygiene, spill response, and outbreak awareness for community settings.',
            durationMin: 40,
            dateDisplay: 'December 28, 2025',
            participants: 27,
          },
          {
            id: 'medication',
            category: 'specialized',
            title: 'Medication support — policy & prompts',
            description:
              'Clarifies prompts-only support, incident reporting, and handover requirements for medication rounds.',
            durationMin: 55,
            dateDisplay: 'November 12, 2025',
            participants: 14,
          },
          {
            id: 'behaviour',
            category: 'specialized',
            title: 'Positive behaviour support — foundations',
            description:
              'Trauma-informed responses, de-escalation basics, and documenting behaviour plans consistently.',
            durationMin: 60,
            dateDisplay: 'November 3, 2025',
            participants: 9,
          },
        ],
        newProgram: {
          metaTitle: 'Create New Training Program',
          breadcrumbNavLabel: 'New training navigation',
          breadcrumbHub: 'Training Hub',
          breadcrumbCurrent: 'New Training',
          breadcrumbEdit: 'Edit Training',
          loadProgramErrorTitle: 'Could not load training program',
          loadProgramErrorBody: 'Return to the Training Hub and try again.',
          pageTitle: 'Create New Training Program',
          pageSubtitle:
            'Define mandatory competencies and certifications required for service delivery.',
          stepperLabel: 'Training program steps',
          steps: {
            programDetails: 'Program Details',
            modules: 'Modules',
            knowledgeCheck: 'Knowledge Check',
            preview: 'Preview',
          },
          step1: {
            sectionTitle: 'Training Program Details',
            programNameLabel: 'Training Program Name',
            programNamePlaceholder: 'e.g., High-Intensity Seizure Management',
            categoryLabel: 'Category',
            categoryPlaceholder:
              'Type category e.g., "Onboarding", "Specialised and Complex Care", "NDIS Mandatory and Foundation", etc.',
            descriptionLabel: 'Description',
            descriptionPlaceholder:
              'Detail the learning outcomes, NDIS Practice Standards met, and key takeaways.',
            mandatoryForAllLabel: 'Mandatory for all staff',
            mandatoryForAllHint:
              'When enabled, this program is automatically assigned to every new staff invite.',
            mandatoryRolesLabel: 'Mandatory for roles',
            mandatoryRolesPlaceholder: 'Select roles when not mandatory for all staff',
          },
          step2: {
            lessonIdentitySectionTitle: 'Lesson Identity & Sequence',
            educationalContentSectionTitle: 'Educational Content',
            lessonTitleLabel: 'Lesson Title',
            lessonTitlePlaceholder: 'e.g., Step 1: Identifying Seizure Aura',
            displayOrderLabel: 'Display Order',
            displayOrderPlaceholder: 'e.g. 1',
            estimatedDurationLabel: 'Estimated duration',
            estimatedDurationAutoSuffix: '(auto)',
            estimatedDurationPending: 'Duration is calculated automatically when you upload a module video.',
            descriptionLabel: 'Description',
            descriptionPlaceholder:
              'Type your lesson content here. You can include tables, bullet points, and formatting...',
            videoLabel: 'Module video',
            videoRequiredMark: '*',
            moduleVideoUpload: {
              selectFile: 'Choose file',
              noFileSelected: 'No video selected',
              removeFile: 'Remove',
              preparing: 'Preparing video…',
              uploading: 'Uploading video',
              finalizing: 'Saving video securely…',
              uploadSuccess: 'Video uploaded successfully.',
              uploadFailed: 'Video upload failed. Choose the file and try again.',
              uploadCancelled: 'Upload cancelled.',
              fileTooLarge: 'Video must be 1 GB or smaller.',
              fileTypeInvalid: 'Unsupported video type. Use MP4, WebM, MOV, or M4V.',
              uploadTimeout: 'Upload timed out. Check your connection and try again.',
              uploadNetworkError: 'Network error during upload. Check your connection and try again.',
              cancelUpload: 'Cancel upload',
              helperText:
                'Upload MP4, WebM, MOV, or M4V. The video is stored securely for staff playback.',
              sizeLimit: 'Maximum file size: 1 GB.',
            },
            requireAcknowledgementLabel: 'Require Acknowledgement',
            acknowledgementWordingLabel: 'Acknowledgement Wording',
            acknowledgementWordingPlaceholder:
              'e.g., I confirm I have watched the safety video and understand the hoist protocols.',
            addModule: 'Add New Module',
            removeModule: 'Remove module',
            expandModule: 'Expand module',
            collapseModule: 'Collapse module',
            moduleUntitled: 'New lesson',
            moduleNeedsAttention: 'Needs attention',
          },
          step3: {
            sectionQuizTitle: 'Quiz configuration',
            sectionQuestionsTitle: 'Questions',
            passingScoreLabel: 'Passing score',
            passingScorePlaceholder: 'e.g., 80%',
            questionTextLabel: 'Question text',
            questionPromptLabel: 'Question text',
            questionPromptPlaceholder:
              "e.g., 'What is the first step when a participant has a Grand Mal seizure?'",
            questionTypeLabel: 'Question type',
            questionTypePlaceholder: 'Select type',
            answerKeyLabel: 'Answer key',
            answerKeyPlaceholder: 'Select answer',
            answerOptionsLabel: 'Answer options',
            optionPlaceholder: 'Type option 1',
            addOption: 'Add another option',
            addQuestion: 'Add question',
            removeQuestion: 'Remove question',
            duplicateQuestion: 'Duplicate',
            requiredLabel: 'Required',
            skip: 'Skip',
          },
          step4: {
            courseContentTitle: 'Course content',
            modulesProgress: '{completed} of {total}',
            contentHeading: 'Content',
            acknowledgementHeading: 'Acknowledgement',
            nextModule: 'Next',
            durationPending: 'Duration pending',
            quizPreviewSummary: 'This program includes a knowledge check with {count} question(s).',
            questionFallback: 'Question',
            noModules: 'No modules added.',
            saveDraft: 'Save as Draft',
            publish: 'Publish',
          },
          saveDraftStubTitle: 'Draft saved',
          saveDraftStubBody: 'Your training program draft is stored locally until the training API ships.',
          saveDraftSuccessTitle: 'Draft saved',
          saveDraftSuccessBody: 'Your training program was saved as a draft.',
          publishSuccessTitle: 'Training Program Published',
          publishSuccessBody: (programName: string) =>
            `${programName} is now live. You can now assign this module to Care Teams and Staff.`,
          saveErrorTitle: 'Could not save training program',
          saveErrorBody: 'Check the highlighted fields and try again.',
          saveValidationToastLabels: {
            programDetails: 'Program details',
            module: (moduleNumber: number) => `Module ${moduleNumber}`,
            knowledgeCheck: 'Knowledge check',
            modules: 'Modules',
            andMore: (count: number) => `…and ${count} more`,
          },
          footer: {
            back: 'Back',
            nextModules: 'Next: Add modules',
            nextKnowledge: 'Next: Knowledge check',
            nextPreview: 'Next: Preview',
          },
          publishStubTitle: 'Training programs coming soon',
          publishStubBody: 'Your draft is ready. Saving to the server will be available when the training API ships.',
          unsavedChangesDialog: {
            title: 'Wait! You have unsaved changes.',
            body: (programName: string) =>
              `You are currently editing the "${programName}" program. If you leave now, any new modules or settings you've added will be lost.`,
            untitledProgramName: 'Untitled program',
            discardCta: 'Discard Changes',
            saveDraftAndExitCta: 'Save as Draft & Exit',
            keepEditingCta: 'Keep Editing',
          },
          validation: {
            programNameRequired: 'Enter a training program name.',
            categoryRequired: 'Enter a category.',
            descriptionRequired: 'Enter a description.',
            moduleTitleRequired: 'Enter a lesson title.',
            displayOrderInvalid: 'Enter a positive whole number (e.g. 1, 2, 3).',
            acknowledgementWordingRequired: 'Enter acknowledgement wording.',
            videoRequired: 'Upload a video file for this module.',
            videoUploadInProgress: 'Wait for the video upload to finish before publishing.',
            videoDurationRequired:
              'Video duration could not be detected. Wait for the upload to finish or re-upload the module video.',
            modulesMinOne: 'Add at least one module.',
            passingScoreRequired: 'Enter a passing score (e.g. 80%).',
            questionsMinOne: 'Add at least one question or skip this step.',
            questionPromptRequired: 'Enter the question text.',
            questionTypeRequired: 'Select a question type.',
            answerKeyRequired: 'Select the correct answer.',
            optionLabelRequired: 'Enter text for each answer option.',
          },
          questionTypes: [
            { value: 'multiple_choice', label: 'Multiple choice (single answer)' },
            { value: 'multi_select', label: 'Multi-select (checkboxes)' },
          ],
        },
      },
      workforce: {
        label: 'Workforce',
        metaTitle: 'Workforce',
        pageTitle: 'Workforce Management',
        description:
          'Manage staff credentials, monitor training compliance, and verify service eligibility.',
        tabAllStaff: 'All Staff',
        tabCareTeams: 'Care Teams',
        tabDocumentsLibrary: 'Documents Library',
        careTeams: {
          loading: 'Loading care teams…',
          loadErrorBody: 'Please try again.',
          searchPlaceholder: 'Search Care Team',
          searchLabel: 'Search care teams',
          createCareTeamCta: 'Create New Care Team',
          sortPlaceholder: 'Sort By',
          sortTeamNameAsc: 'Team name (A–Z)',
          sortTeamNameDesc: 'Team name (Z–A)',
          sortByLocation: 'Location',
          filterLocation: 'Location',
          emptyFiltered: 'No care teams match your filters. Try adjusting search or filters.',
          rowActionsLabel: (name: string) => `Open actions for ${name}`,
          rowMenu: {
            addStaffToTeam: 'Add Staff to Team',
            addParticipantsToTeam: 'Add Participants to Team',
            moveCareTeamSite: 'Move to service site',
            deleteCareTeam: 'Delete Care Team',
          },
          moveCareTeamSiteDialog: {
            title: 'Move care team to service site',
            bodyBefore: 'Choose an active service site for ',
            bodyAfter: '. Team members stay on the team; linked participants keep the same care team name.',
            siteLabel: 'Service site',
            sitePlaceholder: 'Select site',
            loadSitesError: 'Could not load service sites. Please try again.',
            cancelCta: 'Cancel',
            confirmCta: 'Update site',
            confirmBusyCta: 'Updating…',
            successTitle: 'Care team site updated',
            successBody: (teamName: string) => `${teamName} is now assigned to the selected service site.`,
            errorTitle: 'Could not update care team site',
            errorBody: 'Please try again in a moment.',
          },
          deleteCareTeamDialog: {
            title: 'Delete Care Team?',
            titleBulk: (count: number) => `Delete ${count} Care Teams?`,
            bodyBefore: 'You are about to delete the ',
            bodyAfter:
              '. This action is permanent. Any participants currently linked to this team will lose their assigned staff structure, which may disrupt automated rostering.',
            bodyBulkBefore: 'You are about to permanently delete ',
            bodyBulkAfter:
              ' care teams. This action is permanent. Any participants currently linked to these teams will lose their assigned staff structure, which may disrupt automated rostering.',
            typeDeleteLabel: 'Type "DELETE" to confirm.',
            inputPlaceholder: 'DELETE PERMANENTLY',
            typeDeleteValidation: 'Please type DELETE exactly to confirm.',
            cancelCta: 'Cancel',
            confirmCta: (teamName: string) => `Permanently Delete ${teamName}`,
            confirmCtaBulk: (count: number) => `Permanently Delete ${count} Care Teams`,
            confirmBusyCta: 'Deleting…',
            successTitle: 'Care team deleted',
            successBody: (teamName: string) => `${teamName} has been removed from your organisation.`,
            successTitleBulk: 'Care teams deleted',
            successBodyBulk: (count: number) =>
              `${count} ${count === 1 ? 'care team has' : 'care teams have'} been removed from your organisation.`,
            errorTitle: 'Could not delete care team',
            errorTitleBulk: 'Could not delete care teams',
            errorBody: 'Please try again in a moment.',
          },
          table: {
            teamName: 'Team Name',
            siteLocation: 'Site/Location',
            focus: 'Focus',
            assignedParticipants: 'Assigned Participants',
            assignedStaff: 'Assigned Staff',
            actions: 'Actions',
            selectAll: 'Select all care teams',
            selectRow: (name: string) => `Select ${name}`,
            siteArchivedBadge: 'Archived site',
          },
        },
        filterSortBy: 'Sort By',
        filterRole: 'Role',
        filterComplianceStatus: 'Compliance Status',
        filterPrimarySite: 'Primary Site',
        filterSortPlaceholder: 'Sort By',
        filterRolePlaceholder: 'Role',
        filterCompliancePlaceholder: 'Compliance Status',
        filterPrimarySitePlaceholder: 'Primary Site',
        searchPlaceholder: 'Search Staff',
        searchLabel: 'Search staff',
        onboardNewStaff: 'Onboard New Staff',
        bulkDeactivateSelected: 'Deactivate selected?',
        bulkDeactivateNoActiveTitle: 'No active staff selected',
        bulkDeactivateNoActiveBody:
          'Deactivation only applies to staff in Active status. Select one or more active staff members.',
        bulkReactivateSelected: 'Reactivate selected?',
        bulkDeleteSelected: 'Delete selected?',
        table: {
          staffName: 'Staff Name',
          role: 'Role',
          primarySite: 'Primary Site',
          status: 'Status',
          actions: 'Actions',
          selectAll: 'Select all staff',
          selectRow: (name: string) => `Select ${name}`,
        },
        statusActive: 'Active',
        statusInvited: 'Invited',
        statusOngoingTraining: 'Ongoing Training',
        statusDeactivated: 'Deactivated',
        rowActionsLabel: (name: string) => `Open actions for ${name}`,
        rowMenuAssignTrainings: 'Assign Trainings',
        assignTrainingsDialog: {
          title: (staffName: string) => `Assign Trainings to ${staffName}`,
          selectedTrainingsLabel: 'Selected Trainings',
          searchSectionLabel: 'Select Trainings or browse below',
          searchPlaceholder: 'Type to search training',
          searchAriaLabel: 'Search training programs',
          assignCta: 'Assign Trainings',
          assignBusyCta: 'Assigning…',
          loadingLabel: 'Loading training programs…',
          loadErrorTitle: 'Could not load trainings',
          loadErrorBody: 'Check your connection and try again.',
          emptyProgramsTitle: 'No published training programs',
          emptyProgramsBody: 'Publish training programs in the Training Hub before assigning them to staff.',
          assignedBadge: 'Assigned',
          completedBadge: 'Completed',
          alreadyAssignedErrorTitle: 'Already assigned',
          alreadyAssignedErrorBody:
            'Selected trainings are already assigned to this staff member, including completed programs.',
          closeAriaLabel: 'Close',
          removeSelectedAriaLabel: (programName: string) => `Remove ${programName} from selection`,
          successTitle: 'Training assigned',
          successBody: (staffName: string, count: number) =>
            `${count} training program${count === 1 ? '' : 's'} assigned to ${staffName}.`,
        },
        rowMenuManageShifts: 'Manage Shifts',
        rowMenuResetPassword: 'Reset Password',
        rowMenuResendInvite: 'Resend Invite',
        rowMenuDeactivate: 'Deactivate',
        rowMenuDelete: 'Delete',
        rowMenuReactivate: 'Reactivate',
        rowMenuResendInviteSuccessTitle: 'Invite sent',
        rowMenuResendInviteSuccessBody: 'We sent another invite email to this staff member.',
        rowMenuResetPasswordSuccessTitle: 'Password reset sent',
        rowMenuResetPasswordSuccessBody:
          'We emailed them a secure link to set a new password. Their previous password no longer works until they complete the link.',
        rowMenuInvitePasswordActionErrorTitle: 'Could not complete action',
        rowMenuInvitePasswordActionErrorBody: 'Please try again in a moment.',
        reactivateToastTitleSingle: 'Staff Profile Reactivated',
        reactivateToastBodySingle: (name: string) =>
          `${name} can log in to the organisation again and appears in the active directory.`,
        reactivateToastTitleBulk: (count: number) => `${count} Staff Reactivated`,
        reactivateToastBodyBulk: (count: number) =>
          `${count} staff can log in again and are shown as active in the directory.`,
        reactivateErrorTitle: 'Could not reactivate staff',
        reactivateErrorBody: 'Please try again in a moment.',
        sortOptionByStaffName: 'Staff name',
        sortOptionByPrimarySite: 'Primary site',
        roleOptionAll: 'All roles',
        roleOptionCoordinatorManager: 'Coordinator (Manager)',
        roleOptionFrontlineSupportWorker: 'Frontline Staff (Support Worker)',
        roleOptionClinicalRegisteredNurse: 'Clinical Staff (Registered Nurse)',
        complianceOptionAll: 'All statuses',
        complianceOptionCompliant: 'Compliant',
        complianceOptionDue: 'Training due',
        siteOptionAll: 'All sites',
        siteOptionNorthHub: 'North Hub',
        siteOptionClinicalUnit: 'Clinical Unit',
        siteOptionWesternRespite: 'Western Respite',
        siteOptionCommunity: 'Community',
        resetFilters: 'Reset filters',
        loadingStaff: 'Loading staff...',
        loadStaffErrorTitle: 'Unable to load staff',
        loadStaffErrorBody: 'Please try again.',
        emptyInitialTitle: 'Your Workforce Is Empty',
        emptyInitialBody:
          "You haven't added any support workers, coordinators, or nurses to the system yet. You'll need a verified workforce before you can create care teams or assign shifts.",
        emptyCareTeamsTitle: 'No Care Teams Established',
        emptyCareTeamsBody:
          "You haven't defined any care teams yet. Care teams are the structural units used to organize service delivery, manage clinical safety rules, and ensure consistent coverage for participant requirements.",
        emptyCareTeamsCta: 'Create New Care Team',
        emptyFiltered: 'No staff match your filters. Try adjusting search or filters.',
        emptyDocumentsTab: 'The documents library will appear here when content is available.',
        pagination: {
          previous: 'Previous page',
          next: 'Next page',
          summary: (from: number, to: number, total: number) => `Showing ${from}–${to} of ${total}`,
          pageIndicator: (page: number, totalPages: number) => `Page ${page} of ${totalPages}`,
          goToPage: (page: number) => `Go to page ${page}`,
          rowsPerPage: 'Rows per page',
        },
        deactivateDialog: {
          title: 'Deactivate Staff?',
          description: (count: number) =>
            `You are about to deactivate ${count} staff. These staff members will no longer be able to log in to the Salaamah AI mobile app.`,
          descriptionSingle:
            'You are about to deactivate this profile. This staff member will no longer be able to log in to the Salaamah AI mobile app.',
          shiftsWarning: (name: string, shiftCount: number) =>
            `${name} is currently assigned to ${shiftCount} future shifts. Deactivating will leave these shifts unassigned.`,
          reasonLabel: 'Deactivation Reason',
          reasonPlaceholder: 'Select reason',
          reasons: ['Resignation', 'End of Contract', 'Retirement'],
          cancelCta: 'Cancel',
          confirmCta: (count: number) => `Deactivate ${count} staff`,
          confirmSingleCta: 'Confirm Deactivation',
          confirmBusyCta: 'Deactivating…',
          successTitleBulk: (count: number) => `${count} Staff Deactivated`,
          successBodyBulk: (count: number) =>
            `${count} staff have been moved to the inactive directory. Their shifts have been returned to the unassigned pool.`,
          successTitleSingle: 'Staff Profile Deactivated',
          successBodySingle: (name: string, shiftsReturned: number) => {
            const first = `${name} has been moved to the inactive directory.`;
            if (shiftsReturned <= 0) {
              return `${first} Assigned shifts have been returned to the unassigned pool.`;
            }
            const shiftSentence =
              shiftsReturned === 1
                ? '1 shift has been returned to the unassigned pool.'
                : `${shiftsReturned} shifts have been returned to the unassigned pool.`;
            return `${first} ${shiftSentence}`;
          },
          errorTitle: 'Could not deactivate staff',
          errorBody: 'Please try again in a moment.',
        },
        permanentDeleteDialog: {
          titleSingle: 'Permanently Delete Staff Record?',
          titleBulk: (count: number) => `Permanently Delete ${count} Staff Records?`,
          descriptionSinglePrefix: 'You are about to permanently delete ',
          descriptionSingleSuffix: ' from the system. This action is irreversible.',
          descriptionBulk: (count: number) =>
            `You are about to permanently delete ${count} staff records from the system. This action is irreversible.`,
          warningHeading: 'Warning:',
          warningBulletProfile:
            'All profile data, contact info, and document uploads will be purged.',
          warningBulletAuditBulk:
            'Audit Risk: If the staff member has ever completed a shift or signed a medication log, NDIS regulations require you to Deactivate them instead to preserve the audit trail.',
          warningBulletAuditSingle:
            'Audit Risk: If this staff member has ever completed a shift or signed a medication log, NDIS regulations require you to Deactivate them instead to preserve the audit trail.',
          acknowledgementLabel:
            'I understand that deleting a staff member with shift history may result in compliance failures.',
          cancelCta: 'Cancel',
          confirmSingleCta: 'Delete Permanently',
          confirmBulkCta: (count: number) => `Delete ${count} Staff Permanently`,
          confirmBusyCta: 'Deleting…',
          successTitleSingle: 'Staff Record Purged',
          successBodySingle: (name: string) =>
            `${name} has been permanently removed from the database. All associated document files have been deleted.`,
          successTitleBulk: (count: number) => `${count} Staff Record Purged`,
          successBodyBulk: (count: number) =>
            `${count} staff members have been permanently removed from the database. All associated document files have been deleted.`,
          errorTitle: 'Could not delete staff',
          errorBody: 'Please try again in a moment.',
        },
        newStaffPage: {
          metaTitle: 'New Staff',
          breadcrumbNavLabel: 'Workforce breadcrumb',
          breadcrumbHub: 'Workforce Management',
          breadcrumbCurrent: 'New Staff',
          pageTitle: 'New Staff',
          description:
            'Monitor active clinical profiles, risk levels, and care team assignments across the organization.',
          sectionStaffDetails: 'Staff Details',
          sectionRoleEmployment: 'Role & Employment',
          sectionRequiredDocuments: 'Required Documents',
          sectionMandatoryTrainings: 'Mandatory Trainings',
          legalNameLabel: 'Legal First Name & Last Name',
          legalNamePlaceholder: 'e.g. Michael Smith',
          emailLabel: 'Email Address',
          emailPlaceholder: 'e.g. michael.smith@yourprovider.com.au',
          phoneLabel: 'Phone Number',
          phonePlaceholder: 'e.g. 04XX XXX XXX',
          dobLabel: 'Date of Birth',
          dobPlaceholder: 'YYYY-MM-DD',
          dobHint: 'Required for background checks and age-restricted care.',
          residentialAddressLabel: 'Residential Address',
          residentialAddressPlaceholder: 'Unit/Apartment, Street Number/Name, Suburb, Postcode',
          residentialAddressHint: 'Required for compliance records',
          emergencyContactNameLabel: 'Emergency Contact Name',
          emergencyContactNamePlaceholder: 'e.g. John Doe',
          emergencyContactNumberLabel: 'Emergency Contact Number',
          emergencyContactNumberPlaceholder: '04XX XXX XXX',
          systemRoleLabel: 'System Role',
          systemRolePlaceholder: 'Select system role',
          siteLabel: 'Primary site',
          sitePlaceholder: 'Select primary site',
          additionalSitesLabel: 'Additional roster sites',
          additionalSitesPlaceholder: 'Select additional sites (optional)',
          additionalSitesHint:
            'Staff can only be rostered at their primary site and any additional sites you select here.',
          employmentTypeLabel: 'Employment Type',
          employmentTypePlaceholder: 'Select employment type',
          startDateLabel: 'Start Date',
          startDatePlaceholder: 'YYYY-MM-DD',
          requiredDocumentsLabel: 'Select Required Documents',
          requiredDocumentsPlaceholder: 'Type or select required documents',
          mandatoryTrainingsLabel: 'Mandatory Trainings',
          mandatoryTrainingsPlaceholder: 'Type or select mandatory trainings',
          mandatoryTrainingsHint:
            'Pre-filled from role defaults. Adjust before sending the invite if needed.',
          addStaff: 'Add Staff',
          addStaffSubmitting: 'Adding staff…',
          addStaffSuccessTitle: 'Staff member added',
          addStaffSuccessBody: 'The new staff profile was created successfully.',
          addStaffErrorTitle: 'Unable to add staff',
          addStaffErrorBody: 'Please review the form and try again.',
          loadingOptions: 'Loading form options...',
          loadErrorTitle: 'Unable to load new staff form.',
          loadErrorBody: 'Please check backend and try again.',
          retryLoad: 'Retry',
          permissionBannerTitle: 'You do not have permission to add staff.',
          permissionBannerBody: 'Please contact your organisation admin.',
          validation: {
            legalNameRequired: 'Legal full name is required',
            emailInvalid: 'Enter a valid email address',
            phoneRequired: 'Phone number is required',
            dobRequired: 'Date of birth is required',
            residentialAddressRequired: 'Residential address is required',
            emergencyContactNameRequired: 'Emergency contact name is required',
            emergencyContactNumberRequired: 'Emergency contact number is required',
            systemRoleRequired: 'Choose a system role',
            siteRequired: 'Choose a site',
            employmentTypeRequired: 'Choose employment type',
            startDateRequired: 'Start date is required',
          },
        },
        newCareTeamPage: {
          metaTitle: 'New Care Team',
          breadcrumbNavLabel: 'Workforce breadcrumb',
          breadcrumbHub: 'Workforce Management',
          breadcrumbCurrent: 'Add New Care Team',
          pageTitle: 'New Care Team',
          description:
            'Monitor active clinical profiles, risk levels, and care team assignments across the organization.',
          sectionDetails: 'Care Team Details',
          sectionStaff: 'Staff',
          sectionParticipants: 'Participants',
          nameLabel: 'Care Team name',
          namePlaceholder: 'e.g. North Hub Alpha',
          descriptionLabel: 'Description',
          descriptionPlaceholder: 'Briefly describe how this team operates.',
          focusLabel: 'Focus',
          focusPlaceholder: 'e.g. High-intensity physical disability & seizure management',
          siteLabel: 'Site/Location',
          sitePlaceholder: 'Select Site or Location',
          teamLeadLabel: 'Select Team Lead',
          teamLeadPlaceholder: 'Type or select Staff Name',
          addStaffLabel: 'Add Staff',
          addStaffPlaceholder: 'Type or select Staff Name',
          participantsLabel: 'Assign Participants',
          participantsPlaceholder:
            'Type or select participants not already on another care team',
          createTeam: 'Create Team',
          createTeamSubmitting: 'Creating…',
          successTitle: 'Care team created',
          successBody: 'The care team was saved successfully.',
          errorTitle: 'Unable to create care team',
          errorBody: 'Please review the form and try again.',
          loadingOptions: 'Loading form options…',
          loadErrorTitle: 'Unable to load form',
          loadErrorBody: 'Please check your connection and try again.',
          retryLoad: 'Retry',
          permissionBannerTitle: 'You do not have permission to create care teams.',
          permissionBannerBody: 'Please contact your organisation admin.',
          validation: {
            nameRequired: 'Care team name is required',
            siteRequired: 'Choose a site or location',
            teamLeadRequired: 'Select a team lead',
            focusRequired: 'Enter the team focus',
            additionalStaffRequired: 'Add at least one staff member',
            participantsRequired: 'Assign at least one participant',
          },
        },
        staffProfilePage: {
          metaTitle: 'Staff profile',
          tabNavAriaLabel: 'Jump to profile section',
          breadcrumbNavLabel: 'Workforce breadcrumb',
          breadcrumbHub: 'Workforce Management',
          breadcrumbCurrentSuffix: 'Staff profile',
          pageTitlePrefix: 'Staff Profile:',
          resetPassword: 'Reset Password',
          resendInvite: 'Resend Invite',
          deactivate: 'Deactivate',
          delete: 'Delete',
          resendInviteSuccessTitle: 'Invite sent',
          resendInviteSuccessBody: 'We sent another invite email with activation instructions.',
          resetPasswordSuccessTitle: 'Password reset sent',
          resetPasswordSuccessBody:
            'We emailed them a secure link to set a new password. Their previous password no longer works until they complete the link.',
          invitePasswordActionErrorTitle: 'Something went wrong',
          invitePasswordActionErrorBody: 'Please try again or contact support if the problem continues.',
          comingSoonBody: 'This will be available in a future release.',
          deactivateOnlyActiveHint: 'Only staff in Active status can be deactivated.',
          assignTraining: 'Assign Training',
          sendReminder: 'Send Reminder',
          sendReminderNoDocumentsHint:
            'Add required documents to this profile before you can send a compliance reminder.',
          sendReminderSuccessTitle: 'Documents Upload Reminder Sent',
          sendReminderSuccessBody: (staffName: string) =>
            `The initial compliance request has been sent to ${staffName}. They have been notified via email and push notification.`,
          sendReminderErrorTitle: 'Could not send reminder',
          sendReminderErrorBody: 'Please try again in a moment.',
          profilePhotoRemove: 'Remove photo',
          profilePhotoInputAria: 'Upload or change profile photo',
          profilePhotoSuccessTitle: 'Photo updated',
          profilePhotoSuccessBody: 'Profile photo was saved.',
          profilePhotoRemovedTitle: 'Photo removed',
          profilePhotoRemovedBody: 'Profile photo was removed from this profile.',
          profilePhotoErrorTitle: 'Could not update photo',
          profilePhotoErrorBody:
            'Use a JPEG, PNG, or WebP image under 5 MB. If it still fails, check that file storage is configured.',
          editStaffDialog: {
            title: (staffName: string) => `Edit — ${staffName}`,
            closeAria: 'Close edit staff dialog',
            sectionCoreProfile: 'Core Profile',
            sectionStaffDetails: 'Staff Details',
            sectionRoleEmployment: 'Role & Employment',
            roleLockedHint: 'Only an Organisation Admin can change roles.',
            firstNameLabel: 'Legal First Name',
            firstNamePlaceholder: 'e.g., Michael',
            lastNameLabel: 'Legal Last Name',
            lastNamePlaceholder: 'e.g., Smith',
            confirmEdit: 'Confirm Edit',
            confirmBusy: 'Saving…',
            successTitle: 'Profile Updated',
            successBody: (staffName: string) =>
              `Changes to ${staffName}'s profile have been saved successfully.`,
            errorTitle: 'Unable to save',
            errorBody: 'Please review the form and try again.',
            validation: {
              firstNameRequired: 'Legal first name is required',
              lastNameRequired: 'Legal last name is required',
            },
          },
          edit: 'Edit',
          tabCore: 'Core Profile',
          tabTrainings: 'Trainings',
          tabDocuments: 'Documents',
          tabShifts: 'Shifts',
          coreSectionTitle: 'Core Profile',
          trainingSectionTitle: 'Training & Competency',
          trainingLoadingLabel: 'Loading trainings…',
          trainingEmptyTitle: 'No trainings assigned yet',
          trainingEmptyBody:
            "This staff member hasn't been assigned any training modules yet. To ensure they are shift-ready and compliant, assign the required modules for their role.",
          trainingLoadErrorBody: 'Unable to load trainings. Please try again.',
          trainingStatusAssigned: 'Assigned',
          trainingStatusInProgress: 'In progress',
          trainingStatusCompleted: 'Completed',
          trainingStatusOverdue: 'Overdue',
          trainingColTraining: 'Training',
          trainingColProgress: 'Progress',
          trainingColPerformance: 'Performance',
          trainingColDateCompleted: 'Date Completed',
          trainingPerformancePassed: 'Passed',
          trainingPerformanceFailed: 'Failed',
          trainingPerformanceEmpty: '-',
          sendTrainingReminder: 'Send Training Reminder',
          sendTrainingReminderBusy: 'Sending…',
          sendTrainingReminderSuccessTitle: 'Training reminder sent',
          sendTrainingReminderSuccessBody: (staffName: string) =>
            `A reminder was sent to ${staffName} for incomplete training programs.`,
          sendTrainingReminderNoneTitle: 'No reminders to send',
          sendTrainingReminderNoneBody:
            'This staff member has no incomplete training assignments that need a reminder.',
          sendTrainingReminderErrorTitle: 'Could not send reminder',
          sendTrainingReminderErrorBody: 'Please try again in a moment.',
          documentsSectionTitle: 'Documents',
          documentsEmptyTitle: 'No required documents',
          documentsEmptyBody:
            'No compliance document requirements are recorded for this staff member. They are usually set when onboarding new staff.',
          shiftsSectionTitle: 'Upcoming & Active Shifts',
          assignShift: 'Assign Shift',
          shiftStatusActive: 'Active',
          shiftStatusScheduled: 'Scheduled',
          shiftsPreviousWeek: 'Previous week',
          shiftsNextWeek: 'Next week',
          shiftsSelectDay: (label: string) => `View shifts for ${label}`,
          shiftsDayEmpty: 'No shifts scheduled for this day.',
          shiftsLoadErrorBody: 'Unable to load shifts. Please try again.',
          shiftCardNoParticipantSubtitle: 'No participants assigned',
          participantAgeYears: (count: number) => `${count} years`,
          shiftsEmptyTitle: '🛑 Not Eligible for Shifts',
          shiftsEmptyBody:
            'This staff member is currently unqualified for assignment. They must upload their mandatory compliance documents (for example WWCC, NDIS Worker Screening) before they can be added to any roster.',
          fieldFirstName: 'First Name',
          fieldLastName: 'Last Name',
          fieldDateOfBirth: 'Date of birth',
          fieldEmploymentType: 'Employment Type',
          fieldPrimarySite: 'Primary Site',
          fieldAdditionalSites: 'Additional roster sites',
          fieldEmail: 'Email Address',
          fieldPhone: 'Phone Number',
          fieldAddress: 'Address',
          fieldRole: 'System Role',
          fieldStatus: 'Status',
          siteManagement: {
            label: 'Can manage service sites',
            help: 'Lets this coordinator edit and archive their own service sites. Only you can add new sites.',
            enabledToast: 'Site management turned on',
            disabledToast: 'Site management turned off',
            errorTitle: 'Could not update site management',
            errorBody: 'Try again. If it keeps failing, refresh the page.',
          },
          loadErrorTitle: 'Unable to load staff profile',
          loadErrorBody: 'Please try again or return to Workforce Management.',
          notFoundTitle: 'Staff member not found',
          notFoundBody: 'This profile may have been removed or the link is invalid.',
          backToWorkforce: 'Back to Workforce',
          documentsColName: 'Documents Required',
          documentsColReference: 'Reference ID',
          documentsColIssued: 'Issued Date',
          documentsColExpiry: 'Expiry Date',
          documentsColStatus: 'Status',
          documentStatusAwaitingUpload: 'Awaiting upload',
          documentPlaceholder: '—',
          permissionActionsBanner:
            'You can view this profile. Ask an organisation admin to reset passwords, resend invites, deactivate, or delete staff.',
        },
      },
      rosterLogistics: {
        label: 'Shift Management',
        metaTitle: 'Shift Management',
        description: 'Shift Management module page is ready. We can now build the feature content.',
        pageTitle: 'Shift Management',
        pageSubtitle: 'Plan shifts, assign staff, and monitor coverage across your organisation.',
        tabCalendar: 'Calendar',
        tabRegister: 'Shift register',
        todayLabel: 'Today',
        statusFilterLabel: 'Status',
        viewDay: 'Day',
        viewWeek: 'Week',
        viewFortnight: 'Fortnight',
        viewMonth: 'Month',
        viewFilterLabel: 'View',
        statusAllLabel: 'All',
        siteFilterLabel: 'Service sites',
        siteFilterPlaceholder: 'All sites',
        siteFilterEmpty: 'No service sites match',
        siteFilterOneSite: (name: string) => name,
        siteFilterManySites: (count: number) => `${count} sites`,
        newShiftCta: 'New Shift',
        staffSearchPlaceholder: 'Search by staff name or role',
        staffSidebarShiftsOne: '1 shift',
        staffSidebarShiftsMany: (count: number) => `${count} shifts`,
        staffSidebarOnShiftNow: 'On shift now',
        staffSidebarNeedsAttention: 'Draft shifts in this period',
        allStaffLabel: 'All staff',
        loadingCalendar: 'Loading calendar…',
        loadingRegister: 'Loading shift register…',
        filteredEmptyTitle: 'No shifts found',
        filteredEmptyBody:
          'No shifts match your filters. Try adjusting search or status, or clear filters to see all shifts.',
        loadErrorBody: 'We could not load the roster. Try again in a moment.',
        exportRegisterSuccessTitle: 'Shift register exported',
        exportRegisterSuccessBody: (count: number) =>
          `Downloaded ${count} shift${count === 1 ? '' : 's'} as CSV.`,
        exportRegisterErrorTitle: 'Could not export shift register',
        exportRegisterErrorBody: 'Try again in a moment.',
        emptyTitle: 'No shifts in this view yet',
        emptyDescription: 'Create a draft shift or publish your first shift to populate the calendar and register.',
        overlapCluster: {
          popupTitle: 'Shifts at this time',
          cardTitle: 'Shifts at this time',
          shiftsCount: (count: number) => `${count} shifts`,
          moreShifts: (count: number) => `+${count} more`,
          tapToView: 'Tap to view all',
          draftLabel: 'Draft',
        },
        listColumns: {
          dateTime: 'Date & time',
          label: 'Shift',
          location: 'Location',
          participants: 'Participants',
          staff: 'Staff',
          rosterStatus: 'Roster status',
          assignmentStatus: 'Assignment',
          deliveryStatus: 'Delivery',
        },
        registerToolbar: {
          sortLabel: 'Sort by',
          sortPlaceholder: 'Sort by',
          sortOptionSoonest: 'Soonest first',
          sortOptionLatest: 'Latest first',
          sortOptionNewest: 'Newest created',
          sortOptionOldest: 'Oldest created',
          assignmentFilterLabel: 'Assignment',
          assignmentAllLabel: 'All assignments',
          deliveryFilterLabel: 'Delivery',
          deliveryAllLabel: 'All delivery',
          staffFilterLabel: 'Staff',
          staffAllLabel: 'All staff',
          participantFilterLabel: 'Participant',
          participantAllLabel: 'All participants',
          locationFilterLabel: 'Service sites',
          locationAllLabel: 'All sites',
          locationEmptyLabel: 'No service sites available',
          careTypeFilterLabel: 'Care type',
          careTypeAllLabel: 'All care types',
          shiftTypeFilterLabel: 'Shift type',
          shiftTypeAllLabel: 'All shift types',
          dateFromLabel: 'From date',
          dateFromPlaceholder: 'From date',
          dateToLabel: 'To date',
          dateToPlaceholder: 'To date',
          staffSearchPlaceholder: 'Search staff by name',
          staffSearchEmpty: 'No staff match your search',
          participantSearchPlaceholder: 'Search participant by name',
          participantSearchEmpty: 'No participants match your search',
          filtersButton: 'Open filters',
          filtersButtonActive: (count: number) => `${count} filter${count === 1 ? '' : 's'} applied — open filters`,
          filtersDialogTitle: 'Filter shifts',
          filtersDialogSubtitleApplied: (count: number) =>
            `${count} filter${count === 1 ? '' : 's'} selected — apply to update the register`,
          filtersDialogSubtitleNone: 'Choose filters below, then apply to update the register',
          filtersDialogCloseAriaLabel: 'Close filter dialog',
          filtersDialogClearAll: 'Clear all',
          filtersDialogCancel: 'Cancel',
          filtersDialogApply: 'Apply filters',
          advancedFiltersTitle: 'Refine results',
          advancedFiltersSectionSchedule: 'Schedule',
          advancedFiltersSectionPeople: 'People',
          advancedFiltersSectionDetails: 'Shift details',
          advancedFiltersSectionStatus: 'Status',
          appliedFiltersSummary: (count: number) =>
            `${count} active filter${count === 1 ? '' : 's'}`,
          activeFiltersAriaLabel: 'Active filters',
          searchFilterLabel: 'Search',
          dateRangeFilterLabel: 'Date range',
          searchPlaceholder: 'Search by shift, location, staff or participant',
          searchAriaLabel: 'Search shifts',
          resetFilters: 'Reset filters',
          exportRegister: 'Download shift register',
          exportingRegister: 'Preparing download…',
        },
        pagination: {
          previous: 'Previous page',
          next: 'Next page',
          summary: (from: number, to: number, total: number) => `Showing ${from}–${to} of ${total}`,
          pageIndicator: (page: number, totalPages: number) => `Page ${page} of ${totalPages}`,
          goToPage: (page: number) => `Go to page ${page}`,
          rowsPerPage: 'Rows per page',
        },
        createShiftDialog: {
          title: 'Add New Shift',
          saveDraft: 'Save Draft',
          publish: 'Publish Shift',
          cancel: 'Cancel',
          closeAriaLabel: 'Close add shift dialog',
          successDraftTitle: 'Draft Saved Successfully',
          successDraftBody:
            'This shift has been securely saved to your drafts. It will remain hidden from the staff roster and mobile app until you choose to publish it.',
          successPublishedTitle: 'Shift Published Successfully',
          successPublishedBody:
            'This shift is now live on the calendar. Assigned staff members have been notified and can view the details in their mobile app.',
          error: 'We could not save this shift. Check the form and try again.',
          loadingOptions: 'Loading form options…',
          staffHelperText:
            'For safety protocols, the number of staff you selected will determine the limit of patients you can add in a shift.',
          participantsLimit: (limit: number) => `Participants (Limit: ${limit})`,
          validation: {
            participantsCarePlanRequired:
              'Only participants with a signed and active care plan can be added to a shift.',
            staffComplianceRequired:
              'Only staff who have completed required documents and mandatory trainings can be added to a shift.',
            serviceSiteRequired: 'Service site is required',
          },
          fields: {
            label: 'Shift Label',
            labelPlaceholder: 'e.g. Group Outing / SIL Shared Support',
            colour: 'Shift Color',
            colourPlaceholder: 'Pick a color',
            serviceSite: 'Service site',
            serviceSitePlaceholder: 'Select service site',
            location: 'Primary Location',
            locationPlaceholder: 'e.g., "The Community Hub" or "Hilltop Shared Living"',
            date: 'Date',
            datePlaceholder: 'Select Date',
            startTime: 'Start Time',
            endTime: 'End Time',
            staff: 'Staff Member',
            staffPlaceholder: 'Type or select Staff Name',
            participants: 'Participants',
            participantsPlaceholder: 'Type or select Participants',
            instructions: 'Instructions',
            instructionsPlaceholder: 'Write a short instruction',
          },
        },
        viewShiftDialog: {
          closeAriaLabel: 'Close shift details',
          delete: 'delete',
          cancel: 'cancel',
          edit: 'edit',
          publish: 'publish',
          loadError: 'We could not load this shift. Try again in a moment.',
          loadingShift: 'Loading shift details…',
          successPublishedTitle: 'Shift Published Successfully',
          successPublishedBody:
            'This shift is now live on the calendar. Assigned staff members have been notified and can view the details in their mobile app.',
          publishError: 'We could not publish this shift. Try again in a moment.',
          fields: {
            location: 'Primary Location',
            date: 'Date',
            startTime: 'Start Time',
            endTime: 'End Time',
            staff: 'Staff Member',
            participants: 'Participants',
            instructions: 'Instructions',
            cancelReason: 'Cancellation reason',
          },
        },
        updateShiftDialog: {
          title: (label: string) => `Update Shift - ${label}`,
          saveDraft: 'Save Draft',
          update: 'Update Shift',
          cancel: 'Cancel',
          closeAriaLabel: 'Close update shift dialog',
          successDraftTitle: 'Draft Saved Successfully',
          successDraftBody:
            'This shift has been securely saved to your drafts. It will remain hidden from the staff roster and mobile app until you choose to publish it.',
          successLiveTitle: 'Shift Updated & Notifications Sent',
          successLiveBody:
            'Your changes have been published to the live roster. The assigned staff members have been notified of the updates to their schedule.',
          error: 'We could not update this shift. Check the form and try again.',
          loadError: 'We could not load this shift. Try again in a moment.',
          loadingShift: 'Loading shift details…',
          staffHelperText:
            'For safety protocols, the number of staff you selected will determine the limit of patients you can add in a shift.',
          participantsLimit: (limit: number) => `Participants (Limit: ${limit})`,
          validation: {
            participantsCarePlanRequired:
              'Only participants with a signed and active care plan can be added to a shift.',
            staffComplianceRequired:
              'Only staff who have completed required documents and mandatory trainings can be added to a shift.',
            serviceSiteRequired: 'Service site is required',
          },
          fields: {
            label: 'Shift Label',
            labelPlaceholder: 'e.g. Group Outing / SIL Shared Support',
            colour: 'Shift Color',
            colourPlaceholder: 'Pick a color',
            serviceSite: 'Service site',
            serviceSitePlaceholder: 'Select service site',
            location: 'Primary Location',
            locationPlaceholder: 'e.g., "The Community Hub" or "Hilltop Shared Living"',
            date: 'Date',
            datePlaceholder: 'Select Date',
            startTime: 'Start Time',
            endTime: 'End Time',
            staff: 'Staff Member',
            staffPlaceholder: 'Type or select Staff Name',
            participants: 'Participants',
            participantsPlaceholder: 'Type or select Participants',
            instructions: 'Instructions',
            instructionsPlaceholder: 'Write a short instruction',
          },
        },
        deleteShiftDialog: {
          title: 'Are you sure you want to delete this shift?',
          bodyBefore: 'You are about to permanently delete the ',
          bodyMiddle: ' shift scheduled for ',
          bodyAfter:
            '. This action cannot be undone, and the shift will be removed entirely from the calendar and staff rosters.',
          cancelCta: 'Cancel',
          confirmCta: 'Yes, Delete Shift',
          confirmBusyCta: 'Deleting…',
          successTitle: 'Shift Deleted Successfully',
          successBody:
            'The scheduled shift has been completely removed from the calendar and staff rosters.',
          error: 'We could not delete this shift. Try again in a moment.',
        },
        cancelShiftDialog: {
          title: 'Cancel this published shift?',
          bodyBefore: 'You are about to cancel the published ',
          bodyMiddle: ' shift scheduled for ',
          bodyAfter:
            '. The shift will remain on the roster as cancelled for audit purposes and will no longer appear as an active assignment for staff.',
          reasonLabel: 'Cancellation reason',
          reasonPlaceholder: 'Briefly explain why this shift is being cancelled',
          reasonRequired: 'A cancellation reason is required',
          dismissCta: 'Keep shift',
          confirmCta: 'Yes, Cancel Shift',
          confirmBusyCta: 'Cancelling…',
          successTitle: 'Shift Cancelled',
          successBody:
            'This shift has been marked as cancelled. Assigned staff will no longer see it as an active shift.',
          error: 'We could not cancel this shift. Try again in a moment.',
        },
      },
      incidentManagement: {
        label: 'Incident Management',
        metaTitle: 'Incident Management',
        pageTitle: 'Incident Report Tracking & Governance',
        pageSubtitle:
          'Manage, review, and close out participant and staff incident logs under NDIS compliance frameworks.',
        loading: 'Loading incidents…',
        loadListErrorBody: 'We could not load incidents. Try again in a moment.',
        newIncidentCta: 'New Incident Intake',
        emptyTitle: 'All Clear! No Incidents Logged Yet',
        emptyDescription:
          'There are no incident reports registered in this workspace. When an incident is logged, it will appear here in chronological order with its real-time compliance status.',
        listColumns: {
          incidentId: 'Incident ID',
          occurredAt: 'Date & Time',
          participant: 'Participant',
          category: 'Category',
          reporter: 'Reporter',
          status: 'Status',
        },
        statusLabels: {
          draft: 'Draft',
          reported: 'Reported',
          pending_review: 'Pending Review',
          under_review: 'Under review',
          escalated: 'Escalated',
          resolved: 'Resolved',
          closed: 'Closed',
        },
        downloadRegister: 'Download Incident Register',
        downloadRegisterFilename: 'incident-register.csv',
        selectAll: 'Select all incidents',
        selectRow: 'Select incident',
        rowActions: 'Row actions',
        filteredEmpty: 'No incidents match your filters.',
        downloadReportError: 'We could not download this incident report. Try again in a moment.',
        rowActionsMenu: {
          viewDetails: 'View Details',
          downloadReportPdf: 'Download Incident Report (PDF)',
          addManagerReview: 'Add Manager Review',
          continueDraft: 'Continue Draft',
          delete: 'Delete',
        },
        reportPdf: {
          title: 'Incident Report',
          incidentId: 'Incident ID',
          occurredAt: 'Date & Time',
          participant: 'Participant',
          category: 'Category',
          reporter: 'Reporter',
          status: 'Status',
          location: 'Location',
          narrative: 'Incident narrative',
        },
        deleteDialog: {
          title: 'Delete incident draft?',
          bodyBefore: 'You are about to permanently delete ',
          bodyAfter: '. This cannot be undone.',
          warning: 'Only draft incidents can be deleted. Submitted incidents remain in the register.',
          cancelCta: 'Cancel',
          confirmCta: 'Delete draft',
          confirmBusyCta: 'Deleting…',
          error: 'We could not delete this incident draft. Try again in a moment.',
        },
        toolbar: {
          sortLabel: 'Sort By',
          sortPlaceholder: 'Sort By',
          sortOptionNewest: 'Newest first',
          sortOptionOldest: 'Oldest first',
          sortOptionOccurredNewest: 'Date & time (newest)',
          sortOptionOccurredOldest: 'Date & time (oldest)',
          categoryLabel: 'Category',
          categoryPlaceholder: 'Category',
          categoryOptionAll: 'All categories',
          statusLabel: 'Status',
          statusPlaceholder: 'Status',
          statusOptionAll: 'All statuses',
          statusOptionDraft: 'Draft',
          statusOptionReported: 'Reported',
          statusOptionUnderReview: 'Under review',
          statusOptionEscalated: 'Escalated',
          statusOptionResolved: 'Resolved',
          statusOptionClosed: 'Closed',
          searchPlaceholder: 'Search by participant, reporter or incident ID',
          searchAriaLabel: 'Search incidents',
          resetFilters: 'Reset filters',
        },
        pagination: {
          previous: 'Previous page',
          next: 'Next page',
          summary: (from: number, to: number, total: number) => `Showing ${from}–${to} of ${total}`,
          pageIndicator: (page: number, totalPages: number) => `Page ${page} of ${totalPages}`,
          goToPage: (page: number) => `Go to page ${page}`,
          rowsPerPage: 'Rows per page',
        },
        categoryLabels: {
          fall: 'Fall',
          medication_error: 'Medication error',
          behaviour: 'Behaviour / challenging behaviour',
          injury: 'Injury (other)',
          safety_injury: 'Safety/Injury',
          property_damage: 'Property damage',
          safeguarding: 'Safeguarding concern',
          near_miss: 'Near miss',
          missed_shift: 'Missed shift',
          other: 'Other',
        },
        guardianPortalReporter: 'Guardian portal',
        newIntake: {
          metaTitle: 'New Incident Intake',
          breadcrumbNavLabel: 'Incident intake breadcrumb',
          breadcrumbHub: 'Incident Management Hub',
          breadcrumbCurrent: 'New Incident Intake',
          breadcrumbEdit: 'Edit draft',
          breadcrumbView: 'View incident',
          breadcrumbManagerReview: 'Manager review',
          pageTitle: 'New Incident Intake',
          pageTitleView: 'Incident details',
          pageTitleManagerReview: 'Manager review',
          pageSubtitle:
            'Capture what happened, who was involved, and the immediate context before incident gating.',
          pageSubtitleView:
            'Review the submitted incident information across each intake step.',
          pageSubtitleManagerReview:
            'Complete manager review and close out this incident report.',
          stepperAriaLabel: 'Incident intake steps',
          loadingIncident: 'Loading incident…',
          loadingOptions: 'Loading form options…',
          loadOptionsError: 'We could not load form options. Refresh and try again.',
          loadIncidentError: 'We could not load this incident draft.',
          draftEditForbiddenError:
            'Only the person who created this draft can continue editing it.',
          orgAdminDraftForbiddenError:
            'Organisation administrators can only edit draft incidents they created.',
          pendingReviewForbiddenError:
            'Only an organisation administrator can complete manager review for pending incidents.',
          saveDraftError: 'Could not save draft. Check required fields and try again.',
          advanceError: 'Complete all required fields before continuing.',
          submitError: 'Complete all required fields before submitting.',
          toasts: {
            saveDraftSuccessTitle: 'Draft saved',
            saveDraftSuccessBody: 'Your incident draft was saved. You can continue editing anytime.',
            saveDraftErrorTitle: 'Could not save draft',
            saveDraftErrorBody:
              'We could not save your incident draft. Check required fields and try again.',
            submitSuccessTitle: 'Incident submitted',
            submitSuccessBody:
              'The incident has been submitted and is now available in the Incident Management Hub.',
            sendForReviewSuccessTitle: 'Sent for review',
            sendForReviewSuccessBody:
              'The incident has been sent for manager review. An organisation administrator will complete the next step.',
            submitErrorTitle: 'Could not submit incident',
            submitErrorBody:
              'We could not submit this incident. Check required fields and try again.',
          },
          validation: {
            summaryTitle: 'Please check your details',
            summaryBody:
              'Some required fields are missing or incomplete. Review the highlighted fields below.',
            investigationLaunchedRequired: 'Indicate whether an investigation was launched.',
            investigationBriefRequired: 'Upload the Incident Investigation Brief.',
            reportSentToAlliedHealthRequired:
              'Indicate whether the report was sent to allied health professionals.',
            alliedHealthProfessionalsRequired: 'Add allied health professionals involved.',
            reportableIncidentRequired: 'Indicate whether this was a reportable incident.',
            ndisReportDetailsRequired: 'Provide details of the report to the NDIS.',
            managementSummaryRequired: 'Provide a management summary.',
          },
          loadState: {
            errorTitle: 'Could not load incident intake',
            errorBody: 'We could not load the form. Check your connection and try again.',
            retryLoad: 'Try again',
            backToHub: 'Back to Incident Hub',
          },
          unsavedChangesDialog: {
            title: 'Wait! You have unsaved changes.',
            body: 'You are editing this incident draft. If you leave now, your unsaved changes will be lost.',
            discardCta: 'Discard Changes',
            saveDraftAndExitCta: 'Save as Draft & Exit',
            keepEditingCta: 'Keep Editing',
          },
          steps: {
            initialTriage: 'Initial Triage and Metadata',
            incidentGating: 'Incident Gating',
            managerReview: 'Manager Review',
          },
          stepOne: {
            sectionTitle: 'Initial Triage and Metadata',
            classificationLabel: 'Incident Classification',
            classificationHelper: 'This report is being completed to report a',
            participantOption: 'Participant incident/injury',
            staffOption: 'Staff incident/injury',
            categoryLabel: 'Incident Category',
            categoryPlaceholder: 'Type or select incident category',
            occurredAtLabel: 'Date and Time of Incident',
            occurredAtPlaceholder: 'MM/DD/YYYY | 00:00:00',
            involvedParty: {
              label: 'Who was involved?',
              participantPlaceholder: 'Type or select participant name',
              staffPlaceholder: 'Type or select staff name',
              searchAriaLabel: 'Search who was involved',
              loadingLabel: 'Loading…',
              loadMoreLabel: 'Scroll for more',
              emptyLabel: 'No matches found',
              loadErrorLabel: 'Could not load names. Try again.',
            },
            leadUpLabel: 'What happened leading up to/caused the incident?',
            leadUpPlaceholder:
              'Detail the step-by-step timeline, environment, and participant states before the event (e.g., 04:30 AM - Participant woke up, used bathroom, requested coffee at 05:26 AM)...',
            narrativeLabel:
              'Please describe the incident and any actions you made during the incident',
            narrativePlaceholder:
              'Describe the exact nature of the event and any immediate safety, first-aid, or de-escalation protocols applied by staff on the floor...',
            validation: {
              incidentTypeRequired: 'Select an incident classification.',
              incidentCategoryRequired: 'Select an incident category.',
              occurredAtRequired: 'Date and time of incident is required.',
              involvedPartyRequired: 'Select who was involved.',
              leadUpNarrativeRequired:
                'Describe what happened leading up to or caused the incident.',
              incidentNarrativeRequired: 'Describe the incident and any actions taken.',
            },
          },
          stepTwo: {
            sectionTitle: 'Incident Gating & Operational Impact Toggles',
            reportedToTitle: 'Who did you report the incident to?',
            nameLabel: 'Name',
            nameParty: {
              label: 'Name',
              participantPlaceholder: 'Type or select name',
              staffPlaceholder: 'Type or select name',
              searchAriaLabel: 'Search name',
              loadingLabel: 'Loading…',
              loadMoreLabel: 'Scroll for more',
              emptyLabel: 'No matches found',
              loadErrorLabel: 'Could not load names. Try again.',
            },
            positionLabel: 'Position',
            positionPlaceholder: 'Type position',
            reportedAtLabel: 'Date and Time',
            reportedAtPlaceholder: 'MM/DD/YYYY | 00:00:00',
            addAnotherPerson: 'Add Another Person',
            parentCarerLabel: 'Was the parent/carer notified? *Participant incident only',
            firstAidLabel: 'Was there first-aid assistance required after the incident?',
            hospitalLabel: 'Did the incident involve a participant or staff going to hospital?',
            propertyDamageLabel: 'Was there property damage as a result of the incident?',
            yesOption: 'Yes',
            noOption: 'No',
            validation: {
              reportedToMinOne: 'Add at least one person you reported the incident to.',
              staffRequired: 'Select a person.',
              positionRequired: 'Enter a position.',
              reportedAtRequired: 'Date and time is required.',
              parentCarerRequired: 'Indicate whether the parent or carer was notified.',
              yesNoRequired: 'Select yes or no.',
            },
          },
          stepThree: {
            sectionTitle: 'Manager Review',
            sectionRestricted: {
              title: 'Section Restricted:',
              body: 'This section is disabled because it is reserved exclusively for Management Review. Once you send this incident for review, an organisation administrator will complete manager sign-off, investigations, and NDIS reporting before the case is closed.',
            },
            investigationLaunchedLabel: 'Did the incident result in an investigation being launched?',
            investigationLaunchedHelper:
              "Selecting 'Yes' locks the incident report. The case file cannot be officially closed or signed off until the formal Investigation Brief is uploaded and attached.",
            investigationBriefLabel: 'Upload Incident Investigation Brief',
            investigationBriefHelper: 'Upload your completed Incident Investigation Brief here',
            alliedHealthSentLabel:
              'Has the report been sent to relevant allied health professionals?',
            alliedHealthProfessionals: {
              label: 'Allied Health Professionals Involved',
              placeholder: 'Select or type names of health professionals involved..',
              addHint: 'Add another name',
            },
            alliedHealthFeedbackLabel: 'Upload Allied Health Post-Incident Feedback',
            alliedHealthFeedbackHelper:
              'Attach any clinical feedback letters or assessment updates...',
            reportableIncidentLabel: 'Did the incident result in a reportable incident?',
            ndisDetailsLabel: 'Details of Report to NDIS',
            ndisDetailsPlaceholder:
              'Provide precise details of the notification submitted to the NDIS Commission, including the submission date, NDIS portal reference number, and any statutory timeline notes...',
            ndisAcknowledgmentLabel: 'Upload NDIS Commission Acknowledgment / Portal Receipt',
            ndisAcknowledgmentHelper:
              'Ensures an unambiguous digital paper trail for future Quality and Safeguards Commission audits.',
            managementSummaryLabel: 'Management Summary',
            managementSummaryPlaceholder:
              'Log manager review notes, corrective actions, and practitioner consultation instructions (e.g., Liaise with Behaviour Support Practitioner to seek updated response strategies and coordinate staff refresher training)...',
            yesOption: 'Yes',
            noOption: 'No',
            fileUpload: {
              selectFile: 'Select File',
              noFileSelected: 'No file selected',
            },
          },
          footer: {
            saveDraft: 'Save as Draft',
            nextGating: 'Next: Incident Gating',
            nextReview: 'Next: Manager Review',
            back: 'Back',
            finish: 'Submit Incident',
            sendForReview: 'Send for Review',
          },
        },
      },
    },
    guardianDashboard: {
      metaTitle: 'Guardian dashboard',
      breadcrumbHub: 'Dashboard',
      navLabel: 'Dashboard',
      singleWelcomeTitle: (guardianName: string) => `Welcome to Salaamah, ${guardianName}`,
      singleIntroBefore: 'To begin coordinating care for ',
      singleIntroAfter:
        ', we need you to review and sign the current Care Plan. This ensures our team has your explicit consent for all support activities.',
      multiWelcomeTitle: 'Welcome to Salaamah AI Guardian Portal',
      multiIntroBefore:
        'Stay connected with daily progress, upcoming shifts, and the dedicated team supporting ',
      multiIntroAfter: ' journey.',
      viewUpcomingShifts: 'View Upcoming Shifts',
      requestStaffChange: 'Request Staff Change',
      reportConcern: 'Report A Concern',
      carePlanStatusLabel: 'Care Plan Status',
      viewCarePlan: 'View Care Plan',
      reviewAndSignCarePlan: 'Review & Sign Care Plan',
      loading: 'Loading your dashboard…',
      loadErrorTitle: 'Something went wrong',
      loadErrorBody: 'We could not load your participants. Refresh the page or try again later.',
      emptyTitle: 'No participants yet',
      emptyBody:
        'When your provider links a participant to your guardian account, they will appear here.',
      searchEmpty: 'No participants match your search. Try another name or clear the search field.',
      comingSoonTitle: 'Coming soon',
      comingSoonBody: 'This action is not available in the portal yet.',
      awaitingActionsPlaceholder:
        'Your organisation will let you know when your approval or signature is needed.',
      mrnLabel: 'MRN:',
      ndisIdLabel: 'NDIS ID:',
      tabProfileOverview: 'Profile Overview',
      tabCarePlan: 'Care Plan',
      tabUpcomingShifts: 'Upcoming Shifts',
      tabReportedIncidents: 'Reported Incidents',
      tabRequests: 'Requests',
      carePlanLoading: 'Loading care plan…',
      carePlanListTitle: 'Care Plans',
      carePlanListSubtitle:
        'Review the care plans shared with you. Plans awaiting consent need your signature.',
      carePlanListColumnAction: 'Action',
      carePlanListColumnPlanName: 'Plan Name',
      carePlanListColumnPlanType: 'Plan Type',
      carePlanListColumnVersion: 'Version',
      carePlanListColumnStatus: 'Status',
      carePlanListColumnEffectiveFrom: 'Effective From',
      carePlanListColumnReviewDate: 'Review Date',
      carePlanListReviewAndSign: 'Review & Sign',
      carePlanListView: 'View',
      carePlanBackToList: 'Back to care plans',
      carePlanDetailLoading: 'Loading care plan details…',
      carePlanDetailErrorTitle: 'Could not load this care plan',
      carePlanDetailErrorBody: 'Refresh the page or go back and try another plan.',
      carePlanEmptyTitle: 'Plan Under Development',
      carePlanEmptyLine1: (participantName: string) =>
        `We are currently building ${participantName}'s care plan.`,
      carePlanEmptyLine2:
        'Your Coordinator will reach out if we need more detail on their daily routine.',
      carePlanTabErrorTitle: 'Could not load care plan',
      carePlanTabErrorBody: 'Refresh the page or try again in a few moments.',
      upcomingShiftsEmptyTitle: 'No Upcoming Shifts',
      upcomingShiftsEmptyBody:
        'There are no upcoming shifts scheduled for this participant.',
      upcomingShiftsLoading: 'Loading upcoming shifts…',
      upcomingShiftsAssignedStaff: 'Assigned Staff:',
      upcomingShiftsErrorTitle: 'Could not load upcoming shifts',
      upcomingShiftsErrorBody: 'Refresh the page or try again in a few moments.',
      upcomingShiftsLoadingMore: 'Loading more upcoming shifts…',
      reportedIncidentsEmptyTitle: 'All Clear!',
      reportedIncidentsEmptyLine1:
        'No incidents or concerns have been reported for this period.',
      reportedIncidentsEmptyLine2: (participantName: string) =>
        `${participantName}'s safety and support are currently up to date.`,
      reportedIncidentsEmptyCta: 'Report An Incident',
      reportedIncidentsList: {
        loading: 'Loading reported incidents…',
        backToList: 'Back to reported incidents',
        errorTitle: 'Could not load reported incidents',
        errorBody: 'Refresh the page or try again in a few moments.',
        sortReferenceLabel: (direction: 'asc' | 'desc') =>
          direction === 'asc' ? 'Sort reference ascending' : 'Sort reference descending',
        columns: {
          reference: 'Reference #',
          dateFiled: 'Date Filed',
          natureOfConcern: 'Nature of Concern',
          description: 'Description',
          resolutionStatus: 'Resolution Status',
        },
        resolutionStatus: {
          pending: 'Pending',
          in_review: 'In review',
          resolved: 'Resolved',
          closed: 'Closed',
        },
        pagination: {
          previous: 'Previous page',
          next: 'Next page',
          summary: (from: number, to: number, total: number) => `Showing ${from}–${to} of ${total}`,
          pageIndicator: (page: number, totalPages: number) => `Page ${page} of ${totalPages}`,
          goToPage: (page: number) => `Go to page ${page}`,
          rowsPerPage: 'Rows per page',
        },
      },
      reportIncidentForm: {
        pageTitle: 'Feedback & Incident Reporting',
        pageIntro: (participantName: string) =>
          `Your voice is a vital part of ${participantName}'s care. Please use this secure form to report any concerns, safety incidents, or feedback regarding service quality. Our Compliance Team reviews every report within 24 hours.`,
        natureOfConcernLabel: 'Nature of Concern',
        natureOfConcernHelper:
          'Select the option that best describes your concern so we can route it to the right team.',
        natureOfConcernPlaceholder: 'Select category of concern',
        descriptionLabel: 'Description of Event',
        descriptionHelper:
          'Be as specific as possible. Clear details allow for a faster and more accurate investigation.',
        descriptionPlaceholder:
          'Please provide a detailed account of what happened, including any specific conversations or actions taken at the time.',
        occurredAtLabel: 'Date and Time',
        occurredAtHelper: 'If the exact time is unknown, please provide your best estimate.',
        occurredAtPlaceholder: 'Select Date and Time',
        evidenceLabel: 'Upload Evidence (Optional)',
        evidenceHelper:
          'You can upload photos, medical documents, or screenshots. Max file size: 10MB per file.',
        evidenceSelectFile: 'Select File',
        evidenceNoFileSelected: 'No file selected',
        anonymousLabel: 'Keep this report anonymous from the direct support team.',
        anonymousHelper:
          'If checked, the details of this report will only be visible to Senior Management and the Compliance Team.',
        submitCta: 'Submit Incident Report',
        submittingCta: 'Submitting…',
        submitErrorTitle: 'Could not submit report',
        submitErrorBody: (guardianName: string) =>
          `We could not file your report, ${guardianName}. Please try again in a few moments.`,
        evidenceUploadErrorTitle: 'Report filed, evidence not uploaded',
        evidenceUploadErrorBody:
          'Your incident report was saved, but we could not upload the attached file. Try again from the listing or contact support.',
        successToastTitle: (reportReference: string) => `Report #${reportReference} Filed`,
        successToastBody: (guardianName: string) =>
          `Thank you, ${guardianName}. We have received your feedback. You can track the progress of this report in the ‘Reported Incidents’ tab.`,
        concernOptions: {
          safety_injury: 'Safety/Injury',
          medication_error: 'Medication Error',
          property_damage: 'Property Damage',
          conduct_behavior: 'Conduct/Behavior',
          missed_shift: 'Missed Shift',
        },
      },
      requestsEmptyTitle: 'No Active Requests',
      requestsEmptyLine1: 'There are currently no pending staff change requests.',
      requestsEmptyLine2: 'Everything is running smoothly!',
      carePlanSummaryTitle: (versionNo: number) => `Care Plan Summary: v${versionNo}.0`,
      carePlanHeaderParticipantLabel: 'Participant:',
      carePlanHeaderMrnLabel: 'MRN:',
      carePlanHeaderSiteLabel: 'Site:',
      legalConsentTitle: (versionNo: number) => `Legal Consent: Care Plan v${versionNo}.0`,
      legalClauseTitle: 'Legal Clause',
      legalClauseFallbackParticipant: 'this participant',
      legalClauseBody: (participantName: string) =>
        `By signing below, I confirm that I have reviewed the Care Plan for ${participantName}. I consent to the described activities, risk management protocols, and the sharing of this information with the assigned Support Staff to ensure safe delivery of care.`,
      signatureDrawTitle: 'Draw Signature',
      signatureDrawHint: 'Draw your signature here',
      signatureClearCta: 'Clear Signature',
      consentSubmitTitle: 'Consent submitted',
      consentSubmitBody: 'Care plan consent has been recorded successfully.',
      consentSubmitErrorTitle: 'Unable to submit consent',
      consentSubmitErrorBody: 'Please try again in a few moments.',
      consentSubmittingCta: 'Submitting...',
      consentSubmitCta: 'Confirm & Submit Consent',
      governanceNextActionPending: 'Guardian review and consent required.',
      governanceNextActionNone: 'No pending action.',
      noCarePlanStatus: 'No Care Plan',
      noActivitiesListed: 'No activities listed.',
      signedAtLabel: 'Signed At',
      signatureLabel: 'Signature',
      guardianSignatureTitle: 'Guardian Signature',
      selectProviderSectionTitle: 'Select provider',
      signOutCta: 'Sign out',
      switchOrganizationErrorTitle: 'Could not switch organisation',
      switchOrganizationErrorBody: 'Try again or refresh the page.',
    },
    patientsHub: {
      metaTitle: 'Patients Hub',
      pageTitle: 'Patients Hub',
      pageSubtitle:
        'Monitor active clinical profiles, risk levels, and care team assignments across the organization.',
      viewArchivedPatients: 'View Archived Patients',
      archivedListComingSoon: {
        toastTitle: 'Archived patients',
        toastBody: 'Archived patients list view is coming soon.',
      },
      downloadTemplate: 'Download Patients Template',
      importList: 'Import Patients List',
      archiveSelected: 'Archive selected?',
      sendCarePlanReminderSelected: 'Send Care Plan Reminder?',
      bulkImport: {
        fileInputAriaLabel: 'Choose a CSV or Excel file to import (max 500 data rows)',
        errorTitle: 'Could not use file',
        pageBreadcrumbFrom: 'Patients Hub',
        pageBreadcrumbCurrent: 'Import Patients Review',
        pageTitle: 'Review Your File',
        pageDescription:
          'We scanned your file. Please review the entries below and resolve any errors before importing.',
        title: 'Import patients',
        subtitle: (count: number, max: number) =>
          `Previewing ${count} data row${count === 1 ? '' : 's'}. You can import up to ${max} rows per file (excluding the header).`,
        tableHint: (cols: number) =>
          `${cols} column${cols === 1 ? '' : 's'} from the first row of your file.`,
        selectedFileLabel: 'Selected file',
        dateOfBirthLabel: 'Date of birth',
        activeMonitoringLabel: 'Active monitoring',
        columnFallback: (n: number) => `Column ${n}`,
        closeAriaLabel: 'Close import preview',
        reupload: 'Reupload Corrected File',
        cancelImport: 'Cancel Import',
        cancelDialog: {
          title: 'Cancel Import?',
          body: 'Are you sure you want to cancel this import? Any uploaded data and unsaved changes will be lost.',
          continueImport: 'Continue Import',
          confirmCancel: 'Cancel Import',
        },
        confirmDialog: {
          title: 'Confirm Import',
          body: (n: number) =>
            `You're about to import ${n} patient${n === 1 ? '' : 's'}. This action will add new records to your system.`,
          note: 'Note: Only valid rows will be imported.',
          cancel: 'Cancel',
          confirm: 'Confirm Import',
          importing: 'Importing…',
        },
        importSummary: {
          title: 'Import results',
          summaryHeading: 'Summary',
          summaryAddedLabel: 'Added',
          summarySkippedLabel: 'Not imported',
          summaryNarrative: (created: number, skipped: number) => {
            if (created > 0 && skipped === 0) {
              return `All ${created} participant${created === 1 ? '' : 's'} were added successfully.`;
            }
            if (created === 0 && skipped > 0) {
              return `No new participants were added. ${skipped} row${skipped === 1 ? '' : 's'} could not be imported — download the CSV report for full row-by-row reasons.`;
            }
            if (created > 0 && skipped > 0) {
              return `${created} participant${created === 1 ? '' : 's'} added; ${skipped} row${skipped === 1 ? '' : 's'} could not be imported. See row-by-row reasons in the CSV report.`;
            }
            return 'Import finished with nothing new to add.';
          },
          goToHub: 'View Patients Hub',
          downloadReport: 'Download CSV report',
          downloadReportAriaLabel: 'Download a CSV file with the full import results',
          reportFileHint:
            'The CSV lists every imported participant and every not-imported row with the spreadsheet values you entered plus the reason, so you can keep a record or fix your file offline.',
          closeSummaryAriaLabel: 'Close import results',
        },
        selectedCount: (n: number) => `${n} selected`,
        editSelected: 'Edit selected?',
        deleteSelected: 'Delete selected?',
        cancel: 'Cancel',
        confirmImportCta: 'Confirm Import',
        /** Import review toolbar — toggles table edit mode */
        edit: 'Edit',
        saveChanges: 'Save Changes',
        confirmImport: (n: number) => `Import ${n} patient${n === 1 ? '' : 's'}`,
        emptyPreview: 'No data rows found below the header row.',
        errors: {
          invalidType: {
            title: 'Unsupported File Format',
            body: 'Only .xlsx and .csv files are supported.',
          },
          invalidTemplate: {
            title: 'Invalid File Template',
            body: "The file structure doesn't match the required template. Please download and use the latest template.",
          },
          emptyFile: {
            title: 'Empty File',
            body: "The uploaded file doesn't contain any data.",
          },
          tooManyRows:
            'Files can include at most 500 data rows below the header. Split your list and try again.',
          parseFailed:
            'We could not read that file. Export again as CSV or .xlsx and ensure the first sheet has a header row.',
        },
        importSuccessTitle: 'Import complete',
        importSuccessAllBody: (count: number) =>
          `Added ${count} participant${count === 1 ? '' : 's'} to your organisation.`,
        importSuccessPartialBody: (created: number, skipped: number) =>
          `Added ${created} participant${created === 1 ? '' : 's'}. ${skipped} row${skipped === 1 ? '' : 's'} could not be imported (see server response for details).`,
        importFailedTitle: 'Import failed',
        importUnexpectedError: 'Something went wrong while importing. Please try again.',
        editPendingTitle: 'Bulk edit not run yet',
        editPendingBody:
          'Selected rows are ready. Bulk edit actions will be connected when the import API is available.',
        deletePendingTitle: 'Bulk delete not run yet',
        deletePendingBody:
          'Selected rows are ready. Bulk delete actions will be connected when the import API is available.',
        deleteSuccessTitle: 'Entries Deleted Successfully',
        deleteSuccessBody: 'Selected patients have been removed from your list.',
        invalidRowsRemovedTitle: 'Invalid Rows Removed',
        invalidRowsRemovedBody: "All entries look good. You're now ready to import.",
        /** Cross-row duplicate detection in import preview (MRN / NDIS / guardian email). */
        duplicateMrn:
          'This MRN appears on more than one row. Keep one row or assign unique MRNs before import.',
        duplicateNdis:
          'This NDIS number appears on more than one row. Keep one row or assign unique numbers before import.',
        duplicateGuardianEmail:
          'This guardian email appears on more than one row. Keep one row or use distinct addresses before import.',
      },
      newIntake: 'New Intake',
      sortBy: 'Sort By',
      riskFlags: 'Risk Flags',
      monitoringStatus: 'Monitoring Status',
      primaryCareTeam: 'Primary Care Team',
      searchLabel: 'Search participant',
      /** Figma 516:18031 — title case, light placeholder */
      searchPlaceholder: 'Search Participant',
      filterSortPlaceholder: 'Select sort order',
      filterRiskPlaceholder: 'Filter by risk',
      filterMonitoringPlaceholder: 'Filter by monitoring',
      filterTeamPlaceholder: 'Filter by care team',
      resetFilters: 'Reset filters',
      all: 'All',
      loading: 'Loading patients…',
      loadError: 'Could not load patients. Try again.',
      emptyInitialTitle: 'Your Patients Hub is Empty',
      emptyInitialBody:
        "There are no patients registered in the system yet. Once you add or import your patients, you'll be able to manage their Care Plans, safety protocols, and dedicated care teams from this hub.",
      emptyInitialDownloadTemplate: 'Download Patients Template',
      emptyInitialImportList: 'Import Patients List',
      empty: 'No patients match your filters.',
      selectRow: 'Select row',
      rowActions: 'Row actions',
      monitoringActive: 'Active',
      monitoringInactive: 'Inactive',
      columns: {
        nameMrn: 'Name & MRN',
        diagnosis: 'Primary Diagnosis',
        riskFlags: 'Risk Flags',
        primarySite: 'Primary site',
        careTeam: 'Primary care team',
        monitoring: 'Monitoring Status',
        carePlan: 'Care Plan Status',
        actions: '',
      },
      pagination: {
        previous: 'Previous page',
        next: 'Next page',
        summary: (from: number, to: number, total: number) => `Showing ${from}–${to} of ${total}`,
        pageIndicator: (page: number, totalPages: number) => `Page ${page} of ${totalPages}`,
        goToPage: (page: number) => `Go to page ${page}`,
        rowsPerPage: 'Rows per page',
      },
      newIntakePage: {
        metaTitle: 'New Patient Intake',
        pageTitle: 'New Patient Intake',
        /** Figma 516:18189 — matches Patients Hub hero description */
        pageSubtitle:
          'Monitor active clinical profiles, risk levels, and care team assignments across the organization.',
        breadcrumbHub: 'Patients Hub',
        breadcrumbCurrent: 'New Patient Intake',
        breadcrumbNavLabel: 'Go to Patients Hub',
        sectionPatientIdentity: 'Patient Identity',
        sectionGuardian: 'The Guardian',
        sectionClinicalSafety: 'Clinical Safety',
        primaryCareTeamLabel: 'Primary Care Team',
        primaryCarePlaceholder: 'Select care team',
        additionalSitesLabel: 'Additional roster sites',
        additionalSitesPlaceholder: 'Select additional sites (optional)',
        additionalSitesHint:
          'Participants can only be rostered at their primary site and any additional sites you select here.',
        backNavLabel: 'Back to Patients Hub',
        backToHub: 'Patients Hub',
        permissionBannerTitle: 'View only',
        permissionBannerBody:
          'Your role can view the Patients Hub but cannot create new participant profiles. Ask an organisation admin if you need access.',
        loadErrorTitle: 'Could not load',
        loadError: 'We could not verify your workspace. Try again in a moment.',
        loadingOptions: 'Loading workspace options…',
        loadStateErrorBody:
          'We could not load the data needed for this form. Check your connection, try again, or return to the Patients Hub.',
        retryLoad: 'Try again',
        submitCta: 'Add Participant',
        submitSuccessTitle: 'Participant added',
        submitSuccessBody: 'The new profile is available in the Patients Hub.',
        submitErrorTitle: 'Could not save',
        submitError: 'Could not add the participant. Try again in a moment.',
        error403Title: 'Permission denied',
        error403Body: 'You do not have permission to create participant profiles.',
        error409Title: 'Finish onboarding first',
        error409Body: 'Complete organisation onboarding before adding participants from the hub.',
        archivedMatchDialog: {
          title: (participantName: string) => `Record found for ${participantName}`,
          body: (participantName: string) =>
            `A record for ${participantName} already exists in the archive. Participant names and NDIS numbers must be unique across active and archived records. Reactivate that profile to continue with it, or cancel and change the details you entered.`,
          reactivatePatient: 'Reactivate participant',
          reactivateBusy: 'Reactivating…',
        },
      },
      carePlanDraftPage: {
        metaTitle: 'Care Plan Draft',
        breadcrumbNavLabel: 'Care plan draft breadcrumb',
        breadcrumbHub: 'Patients Hub',
        breadcrumbCurrent: 'Draft Care Plan',
        loadErrorTitle: 'Could not load care plan draft',
        loadErrorBody: 'Please try again in a moment.',
        retryLoad: 'Retry',
        title: (name: string) => `${name} : Care Plan`,
        subTitle: (name: string, status: string) =>
          `Creating v.1 for Participant: ${name}, Status: ${status}`,
        creatingLabel: 'Creating',
        participantLabel: 'Participant:',
        statusLabel: 'Status:',
        stepSetup: 'Setup',
        stepTriage: 'Classification & Risk Triage',
        stepModules: 'Module & Plan Stack',
        stepSummary: 'Summary',
        stepWoundAssessment: 'Assessment & Clinical Triage',
        stepWoundTreatment: 'Treatment & Emergency Management',
        woundAssessmentSectionTitle: 'Assessment & Clinical Triage',
        woundTreatmentSectionTitle: 'Treatment & Emergency Management Workspace',
        woundConditionLabel: 'Condition',
        woundSymptomsLabel: 'Symptoms',
        woundChartOnFileLabel: 'Completed Wound Assessment Chart On File?',
        woundChartNotesLabel: 'Wound Assessment Chart Discussion Notes',
        woundTreatmentFormOnFileLabel: 'Completed Wound Assessment Treatment Form On File?',
        woundTreatmentFormCommentsLabel: 'Wound Assessment Treatment Form Comments',
        woundTreatmentHeading: 'Treatment',
        woundAgreedGoalsLabel: 'Agreed Treatment Goals',
        woundAgreedPlanLabel: 'Agreed Treatment Plan',
        woundDressingRequirementsLabel: 'Dressing Requirements',
        woundDressingScheduleLabel: 'Dressing Schedule',
        woundReportToHpLabel: 'Information Which Must Be Reported to the Health Professional',
        woundRisksHeading: 'Risks & Emergencies',
        woundPotentialRisksLabel: 'Potential Risks Identified',
        woundRiskManagementLabel: 'Potential Risk Management Process',
        woundEmergencyOptionsLabel: 'Emergency Management: List Options',
        woundEmergencyProceduresLabel: 'Emergency Management: List Procedures',
        woundChecklistHeading: 'Pre-Activation Checklist',
        woundChecklistPractitionerOrders: 'Health practitioner\u2019s orders for wound care received.',
        woundChecklistWoundChart: 'Wound Assessment Chart completed.',
        woundChecklistTreatmentForm: 'Wound Assessment Treatment Form completed.',
        woundChecklistStaffTrained:
          'Staff trained and competent to assist with wound care management.',
        woundPartnershipHeading: 'Role of Others in Care Plan / Agreed Partnership Action',
        woundPartnershipNameLabel: 'Name',
        woundPartnershipRelationshipLabel: 'Relationship',
        woundPartnershipRoleLabel: 'Role and Responsibilities',
        woundAddPartnershipRole: 'Add New Role & Responsibilities',
        woundRemovePartnershipRole: 'Remove role',
        woundTreatmentChangesLabel: 'Treatment Changes Required?',
        woundOutlineChangesLabel: 'Outline Changes Necessary',
        woundReviewHeading: 'Treatment Review',
        placeholderWoundCondition:
          'e.g., Skin tearing and open lesions on lower limbs secondary to self-injurious wound picking...',
        placeholderWoundSymptoms:
          'Describe physical wound traits: e.g., Open skin lesion on lower right leg, size 3cm x 2cm, localized tissue redness, high risk of slow healing due to Type 2 Diabetes.',
        placeholderWoundChartNotes:
          'Record explicit feedback, pain tolerances, or specific placement inputs captured directly from the participant during the assessment...',
        placeholderWoundTreatmentFormComments:
          'e.g., Discussed the step-by-step cleaning and dressing parameters directly with participant and her representative.',
        placeholderWoundAgreedGoals: 'Define agreed treatment goals for this wound care plan...',
        placeholderWoundAgreedPlan: 'Detail the agreed treatment plan and daily care instructions...',
        placeholderWoundDressingRequirements: 'Specify dressing products and technique requirements...',
        placeholderWoundDressingSchedule: 'Describe frequency and timing of dressing changes...',
        placeholderWoundReportToHp:
          'List signs and symptoms that must be reported to the health professional...',
        placeholderWoundPotentialRisks: 'Select potential risks',
        placeholderWoundRiskManagement: 'Describe how identified risks will be managed...',
        placeholderWoundEmergencyOptions: 'List emergency management options available to staff...',
        placeholderWoundEmergencyProcedures: 'Document step-by-step emergency procedures...',
        placeholderWoundPartnershipName: 'e.g. John Doe',
        placeholderWoundPartnershipRelationship: 'Select relationship',
        placeholderWoundPartnershipRole: 'Describe their role and responsibilities...',
        placeholderWoundOutlineChanges: 'Outline any treatment changes that are necessary...',
        woundRiskSlowedTissueHealing: 'Slowed tissue healing (diabetic complication)',
        woundRiskSecondaryInfection: 'Secondary clinical infection risk',
        woundRiskMechanicalWoundPicking: 'Severe skin tearing via mechanical wound-picking',
        woundRiskPressureInjuryBreakdown: 'Pressure injury breakdown',
        woundRelationshipLegalGuardian: 'Legal Guardian / Advocate',
        woundRelationshipParent: 'Parent',
        woundRelationshipSibling: 'Sibling',
        woundRelationshipSpousePartner: 'Spouse / Partner',
        woundRelationshipSupportCoordinator: 'Support Coordinator',
        woundRelationshipSupportWorker: 'Support worker',
        woundRelationshipClinician: 'Clinician',
        woundRelationshipOther: 'Other',
        requiredChecklistItem: 'Confirm this checklist item to continue',
        requiredPartnershipName: 'Name is required',
        requiredPartnershipRelationship: 'Relationship is required',
        requiredPartnershipRole: 'Role and responsibilities are required',
        requiredAtLeastOnePartnershipRole: 'Add at least one partnership role',
        nextWoundAssessment: 'Next: Assessment & Clinical Triage',
        nextWoundTreatment: 'Next: Treatment & Emergency Management',
        backWoundAssessment: 'Back: Assessment & Clinical Triage',
        backWoundTreatment: 'Back: Treatment & Emergency Management',
        titleWound: (name: string) => `${name} : Wound Management Care Plan`,
        summarySectionWoundAssessment: 'Assessment and Clinical Triage',
        summarySectionWoundTreatment: 'Treatment and Emergency Management',
        summaryWoundChartCompleted: 'Completed Wound Assessment Chart',
        summaryWoundTreatmentFormCompleted: 'Completed Wound Assessment Treatment Form',
        summaryWoundChartNotes: 'Wound Assessment Chart Notes',
        summaryWoundFormComments: 'Wound Assessment Form Comments',
        summaryWoundEmergencyOptions: 'Emergency Management Options',
        summaryWoundEmergencyProcedures: 'Emergency Management Procedures',
        summaryWoundOutlineChanges: 'If Yes, Outline Changes Necessary',
        stepEnteralInterventions: 'Interventions, Goals & Delivery Details',
        stepEnteralAdministration: 'Enteral Administration, Risk & Emergency Management',
        stepEnteralMealtime: 'Mealtime Preparation & Nutritional Dosage Matrix',
        titleEnteral: (name: string) => `${name} : Enteral Feed Care Plan`,
        enteralInterventionsSectionTitle: 'Interventions, Goals & Delivery Details',
        enteralAdministrationSectionTitle: 'Enteral Administration, Risk & Emergency Management',
        enteralMealtimeSectionTitle: 'Mealtime Preparation & Nutritional Dosage Matrix',
        enteralConditionLabel: 'Condition',
        enteralSymptomsLabel: 'Symptoms',
        enteralInterventionsLabel: 'Interventions',
        enteralGoalsLabel: 'Goals of Interventions',
        enteralTreatmentPlanLabel: 'Treatment plan',
        enteralPreparationDeliveryLabel: 'Preparation and delivery details',
        enteralAdministrationLabel: 'Administration',
        enteralInsertionSiteLabel: 'Care of insertion site and enteral feeding tube',
        enteralMedicationAdminLabel: 'Medication administration via an enteral tube',
        enteralPotentialRisksLabel: 'Potential risks and incidents with enteral feeding',
        enteralRiskHeading: 'Potential risk and incident management',
        enteralRiskProceduresLabel: 'List Procedures',
        enteralEmergencyHeading: 'Emergency management',
        enteralEmergencyOptionsLabel: 'List options',
        enteralEmergencyProceduresLabel: 'List Procedures',
        enteralStakeholderHeading: 'Stakeholder Roles & Responsibilities',
        enteralStakeholderRoleLabel: 'Stakeholder Role',
        enteralStakeholderResponsibilitiesLabel: 'Roles and Responsibilities',
        enteralAddStakeholderRole: 'Add New Role & Responsibilities',
        enteralRemoveStakeholderRole: 'Remove role',
        enteralNutritionalHeading: 'Nutritional Requirements',
        enteralMacroLabel: 'Macro',
        enteralEstimatedRequirementsLabel: 'Estimated Requirements',
        enteralFullRegimenProvidesLabel: 'Full Regimen Provides',
        enteralAddMacro: 'Add Macro',
        enteralInitialRegimenHeading: 'Initial Regimen',
        enteralFullRegimenHeading: 'Full Regimen',
        enteralRegimenDateLabel: 'Date',
        enteralFeedTypeLabel: 'Feed Type',
        enteralVolumeLabel: 'Volume of Feed (ml)',
        enteralRateLabel: 'Rate (ml/hr)',
        enteralDurationLabel: 'Duration of feed (hrs)',
        enteralAdditionalInfoLabel: 'Additional information',
        enteralAddRegimen: 'Add Regimen',
        enteralRemoveRow: 'Remove',
        enteralDieticianLabel: 'Dietician',
        enteralDateLabel: 'Date',
        enteralReviewDateLabel: 'Review Date',
        enteralChecklistHeading: 'Pre-Activation Checklist',
        enteralChecklistVerificationHeading: 'Pre-Activation Checklist Verification',
        enteralChecklistMealtimePlan:
          'Mealtime preparation and delivery plan completed and available for staff and participants.',
        enteralChecklistOrgProcedures:
          'Organisational procedures and resources printed and available.',
        enteralChecklistMedicationPlan:
          'Medication plan up to date and medication available if required.',
        enteralChecklistStaffTrained:
          'Staff trained and competent in enteral feeding and infection control.',
        enteralChecklistRnCompetent:
          'Registered Nurse is competent in managing enteral feeding and tube replacement.',
        enteralPartnershipHeading: 'Role of Others in Care Plan / Agreed Partnership Action',
        enteralPartnershipNameLabel: 'Name',
        enteralPartnershipRelationshipLabel: 'Relationship',
        enteralPartnershipRoleLabel: 'Role and Responsibilities',
        enteralAddPartnershipRole: 'Add New Role & Responsibilities',
        enteralRemovePartnershipRole: 'Remove role',
        enteralInterventionPegFeeding: 'PEG feeding',
        enteralInterventionTwelveHourSupport: '12 Hour support staff',
        enteralInterventionMealtimeSupport: 'Mealtime support',
        enteralInterventionSevereDysphagia: 'Severe dysphagia Management',
        enteralInterventionBowelManagement: 'Bowel Management',
        enteralInterventionManualHandling: 'Manual Handling',
        enteralInterventionOtherSpecialized: 'Other Specialized Care',
        placeholderEnteralCondition: 'e.g., Multiple Sclerosis (MS)',
        placeholderEnteralSymptoms: 'e.g., Completely Dependant, Non Verbal',
        placeholderEnteralInterventions: 'Select interventions',
        placeholderEnteralGoals: 'e.g., To gain weight and maintain adequate hydration...',
        placeholderEnteralTreatmentPlan: 'Outline the treatment plan for enteral feeding...',
        placeholderEnteralPreparationDelivery:
          'Outline step-by-step preparation and delivery details...',
        placeholderEnteralAdministration: 'e.g., Bolus feed via PEG four times daily...',
        placeholderEnteralInsertionSite: 'Document care of the insertion site and feeding tube...',
        placeholderEnteralMedicationAdmin:
          'e.g., Flush with water before and after medications...',
        placeholderEnteralPotentialRisks: 'e.g., Aspiration, site infection, tube blockage...',
        placeholderEnteralRiskProcedures: 'Document risk management procedures...',
        placeholderEnteralEmergencyOptions: 'List emergency scenarios staff may encounter...',
        placeholderEnteralEmergencyProcedures: 'Document emergency response procedures...',
        placeholderEnteralStakeholderRole: 'e.g., Support Worker',
        placeholderEnteralStakeholderResponsibilities: 'Describe roles and responsibilities...',
        placeholderEnteralDietician: 'e.g., John Doe',
        placeholderEnteralPartnershipName: 'e.g., Jane Doe',
        placeholderEnteralPartnershipRelationship: 'Select relationship',
        placeholderEnteralPartnershipRole: 'Describe roles and responsibilities...',
        placeholderEnteralMacro: 'e.g., Energy (kcal)',
        placeholderEnteralEstimated: 'e.g., 1800',
        placeholderEnteralFullProvides: 'e.g., 1850',
        placeholderEnteralFeedType: 'e.g., Nutrison',
        placeholderEnteralVolume: 'e.g., 250',
        placeholderEnteralRate: 'e.g., 50',
        placeholderEnteralDuration: 'e.g., 5',
        placeholderEnteralAdditionalInfo: 'Optional notes...',
        requiredEnteralStakeholderRole: 'Stakeholder role is required',
        requiredEnteralStakeholderResponsibilities: 'Roles and responsibilities are required',
        requiredAtLeastOneStakeholderRole: 'Add at least one stakeholder role',
        requiredAtLeastOneMacro: 'Add at least one nutritional requirement row',
        requiredAtLeastOneRegimen: 'Add at least one regimen row',
        nextEnteralInterventions: 'Next: Interventions, Goals & Delivery Details',
        nextEnteralAdministration: 'Next: Enteral Administration, Risk & Emergency Management',
        nextEnteralMealtime: 'Next: Mealtime Preparation & Nutritional Dosage Matrix',
        backEnteralInterventions: 'Back: Interventions, Goals & Delivery Details',
        backEnteralAdministration: 'Back: Enteral Administration, Risk & Emergency Management',
        backEnteralMealtime: 'Back: Mealtime Preparation & Nutritional Dosage Matrix',
        summarySectionEnteralInterventions: 'Intervention, Goals & Delivery Details',
        summarySectionEnteralAdministration: 'Enteral Administration, Risk & Emergency Management',
        summarySectionEnteralMealtime: 'Mealtime Preparation & Dietician Dosage Matrix',
        stepBowelProfile: 'Bowel Profile',
        stepBowelInterventions: 'Interventions & Daily Treatment Plan',
        stepBowelRisks: 'Risks, Emergencies & Operational Routing',
        titleBowel: (name: string) => `${name} : Bowel Management Care Plan`,
        bowelProfileSectionTitle: 'Bowel Profile',
        bowelInterventionsSectionTitle: 'Intervention & Daily Treatment Plan',
        bowelRisksSectionTitle: 'Risks, Emergencies & Operational Routing',
        bowelStoolAppearanceLabel: 'Normal stool appearance',
        bowelStoolType1: 'Type 1',
        bowelStoolType2: 'Type 2',
        bowelStoolType3: 'Type 3',
        bowelStoolType4: 'Type 4',
        bowelStoolType5: 'Type 5',
        bowelStoolType6: 'Type 6',
        bowelStoolType7: 'Type 7',
        bowelNormalHabitsLabel: 'Normal Bowel Habits',
        bowelSymptomsLabel: 'Symptoms',
        bowelImmediateActionLabel: 'Symptoms that require immediate action',
        placeholderBowelNormalHabits:
          "Describe the participant's usual bowel frequency, timing, and consistency...",
        placeholderBowelSymptoms: 'Select symptoms',
        placeholderBowelImmediateAction: 'Select symptoms requiring immediate action',
        bowelSymptomConstipation: 'Constipation (infrequent, hard stools, difficulty passing)',
        bowelSymptomDiarrhea: 'Diarrhea (frequent, watery stools)',
        bowelSymptomIncompleteEvacuation: 'Incomplete evacuation sensation',
        bowelSymptomAbdominalBloating: 'Abdominal bloating or discomfort',
        bowelSymptomStraining: 'Straining during bowel movements',
        bowelSymptomFaecalIncontinence: 'Faecal incontinence',
        bowelImmediateActionSevereConstipation: 'Severe constipation lasting more than 3-5 days',
        bowelImmediateActionPersistentDiarrhea: 'Persistent diarrhea (lasting more than 24 hours)',
        bowelImmediateActionSevereAbdominalPain: 'Severe abdominal pain, bloating, or distension',
        bowelImmediateActionUnexplainedWeightLoss: 'Unexplained weight loss',
        bowelImmediateActionSuddenBowelIncontinence: 'Sudden onset of bowel incontinence',
        bowelImmediateActionLossOfAppetite: 'Loss of appetite',
        bowelInterventionsLabel: 'Interventions',
        bowelInterventionGoalsLabel: 'Intervention goals',
        bowelInterventionTimingLabel: 'Intervention implementation timing',
        bowelTreatmentPlanLabel: 'Treatment plan',
        bowelMedicationProceduresLabel: 'Medication selection and administration procedures',
        placeholderBowelInterventions:
          'Outline specific protocols for Dietary Adjustments, Physical Activity, Scheduled Toileting, Laxatives/Stool Softeners, Pelvic Floor Exercises, and Digital Stimulation/Suppositories...',
        placeholderBowelInterventionGoals:
          'e.g., Establish regular, predictable bowel movements; Reduce episodes of constipation or diarrhea; Prevent faecal incontinence; Improve patient comfort and quality of life.',
        placeholderBowelInterventionTiming:
          'e.g., Daily monitoring of bowel habits; Adjustments to diet/hydration as needed; Medication use based on symptom severity.',
        placeholderBowelTreatmentPlan:
          'Detail modifications, hydration optimization, fiber supplements, laxative schedules, and routine bowel programs...',
        placeholderBowelMedicationProcedures:
          'Specify names of prescribed laxatives/softeners, exact dosage parameters, and clinical logic triggers based on Bristol Stool consistency scores...',
        bowelPotentialRisksLabel: 'Potential risks with bowel care',
        placeholderBowelPotentialRisks: 'Select potential risks',
        bowelRiskLaxativeDependency: 'Overuse of laxatives leading to dependency',
        bowelRiskDehydrationFromLaxatives:
          'Dehydration due to excessive laxative use or diarrhea',
        bowelRiskBowelObstruction: 'Risk of bowel obstruction from untreated constipation',
        bowelRiskSkinIrritation:
          'Skin irritation or pressure sores from faecal incontinence',
        bowelEmergencyHeading: 'Emergency management',
        bowelEmergencyOptionsLabel: 'List Options',
        bowelEmergencyProceduresLabel: 'List Procedures',
        placeholderBowelEmergencyOptions:
          'e.g., Persistence of severe constipation, persistent diarrhea, signs of bowel obstruction, severe pain, or bleeding...',
        placeholderBowelEmergencyProcedures:
          'e.g., Digital rectal stimulation if recommended, administration of suppositories or enemas, adjusting medication...',
        bowelReportToHealthProfessionalLabel:
          'Information which must be reported to a health professional',
        placeholderBowelReportToHealthProfessional:
          'e.g., Significant changes in bowel habits, persistent constipation/diarrhea despite intervention, pain, bloating, or rectal bleeding.',
        bowelManagementInstructionsLabel:
          'Specific instructions, details and considerations for management and changes',
        placeholderBowelManagementInstructions:
          'Include long-term tracking expectations, fluid adjustment rules based on output consistency, and routine follow-up windows...',
        bowelChecklistHeading: 'Pre-Activation Checklist',
        bowelChecklistDoctorsOrders: 'Doctor\u2019s orders for bowel care are completed.',
        bowelChecklistBowelCareChart: 'Bowel Care Chart in place.',
        bowelChecklistStaffTrained: 'Staff trained and competent to assist with bowel care.',
        bowelChecklistUsualHabitsRecorded: 'Participant\u2019s usual bowel habits recorded.',
        bowelPartnershipHeading: 'Role of Others in Care Plan / Agreed Partnership Action',
        bowelPartnershipNameLabel: 'Name',
        bowelPartnershipRelationshipLabel: 'Relationship',
        bowelPartnershipRoleLabel: 'Role and Responsibilities',
        bowelAddPartnershipRole: 'Add New Role & Responsibilities',
        bowelRemovePartnershipRole: 'Remove role',
        placeholderBowelPartnershipName: 'e.g., John Doe',
        placeholderBowelPartnershipRelationship: 'Select relationship',
        placeholderBowelPartnershipRole:
          'e.g., Family member will monitor clothing barriers at home, ensure the participant wears protective lower-limb sleeves during family outings, and immediately notify the organization if any dressing shifting or wound-picking behaviors are observed.',
        nextBowelProfile: 'Next: Bowel Profile',
        nextBowelInterventions: 'Next: Interventions & Daily Treatment Plan',
        nextBowelRisks: 'Next: Risks, Emergencies & Operational Routing',
        backBowelProfile: 'Back: Bowel Profile',
        backBowelInterventions: 'Back: Interventions & Daily Treatment Plan',
        backBowelRisks: 'Back: Risks, Emergencies & Operational Routing',
        summarySectionBowelProfile: 'Bowel Profile',
        summarySectionBowelInterventions: 'Intervention and Daily Treatment Plan',
        summarySectionBowelRisks: 'Risks, Emergencies & Operational Routing',
        bowelChecklistVerificationHeading: 'Pre-Activation Checklist Verification',
        yesLabel: 'Yes',
        noLabel: 'No',
        progressStatus: 'Progress Status',
        triageSectionTitle: 'Classification and Risk Triage',
        triageRoutineDailyLivingOnly: 'Routine Daily Living Support Only?',
        triageRoutineDailyLivingOnlyHelper:
          'Selecting Yes indicates no high-intensity clinical needs, allowing a standard daily support path to execute.',
        triageContinenceManagement: 'Continence Management Required?',
        triageCommunicationSupport: 'Communication Support Required?',
        triageMedicationAdministration: 'Medication Administration Support Needed?',
        triageMobilityManualHandling: 'Mobility / Manual Handling Risk?',
        triageNutritionHydration: 'Nutrition and Hydration Support needed?',
        triageBehaviourPsychosocial: 'Behaviour/Psychosocial Care needed?',
        triagePersonalCare: 'Personal care needed?',
        triageSkinIntegrity: 'Skin Integrity required?',
        triageRiskEscalation: 'Risk and Emergency Escalation needed?',
        requiredTriageAnswer: 'Select Yes or No to continue',
        modulesSectionTitle: 'Module & Plan Stack',
        yourModulesTitle: 'Your Modules',
        markModuleComplete: 'Mark as complete',
        moduleMarkedComplete: 'Module complete',
        careActionsSectionTitle: 'Care Actions (Staff Responsibilities)',
        participantPreferencesLabel: 'Participant Preferences / Communication Notes',
        placeholderParticipantPreferences:
          'Detail preferred morning routines, cultural observations, or communication styles...',
        moduleDailySupportTitle: 'Daily Support',
        moduleDailySupportChip: 'Daily Support',
        moduleCommunicationTitle: 'Communication Support',
        moduleCommunicationChip: 'Communication',
        moduleContinenceTitle: 'Continence Care',
        moduleContinenceChip: 'Continence',
        moduleMedicationTitle: 'Medication Support',
        moduleMedicationChip: 'Medication',
        moduleMobilityTitle: 'Mobility / Manual Handling',
        moduleMobilityChip: 'Mobility',
        moduleSkinIntegrityTitle: 'Skin Integrity',
        moduleSkinIntegrityChip: 'Skin Integrity',
        modulePersonalCareTitle: 'Personal Care',
        modulePersonalCareChip: 'Personal Care',
        moduleBehaviourTitle: 'Behaviour / Psychosocial',
        moduleBehaviourChip: 'Behaviour',
        moduleRiskEscalationTitle: 'Risk and Emergency Escalation',
        moduleRiskEscalationChip: 'Risk Escalation',
        moduleNutritionTitle: 'Nutrition & Hydration',
        moduleNutritionChip: 'Nutrition & Hydration',
        activityType: 'Activity Type',
        timeWindow: 'Time Window',
        placeholderSelectTimeWindow: 'Select Time Window',
        placeholderMandatoryYesNo: 'Yes/No',
        placeholderDailySupportCareObjectives:
          'e.g., Increase community access and physical stamina by attending lawn bowls...',
        placeholderDailySupportActivityDescription:
          'e.g., Administer 50mg Sertraline. Ensure Sarah has finished her breakfast first.',
        activityHeader: (index: number, name: string) => `Activity ${index}: ${name}`,
        activityHeaderEmpty: (index: number) => `Activity ${index}`,
        timeWindowEarlyMorning: 'Early Morning (5:00 AM – 8:00 AM)',
        timeWindowMorning: 'Morning (8:00 AM – 12:00 PM)',
        timeWindowMidday: 'Midday (12:00 PM – 2:00 PM)',
        timeWindowAfternoon: 'Afternoon (2:00 PM – 5:00 PM)',
        timeWindowEvening: 'Evening (5:00 PM – 9:00 PM)',
        timeWindowNight: 'Night (9:00 PM – 5:00 AM)',
        timeWindowAsRequired: 'As Required',
        timeWindowStart: 'Start time',
        timeWindowEnd: 'End time',
        placeholderActivityType: 'Select activity type',
        placeholderSelectTime: 'Select time',
        placeholderSelectMandatory: 'Select yes / no',
        activityTypePersonalCare: 'Personal Care',
        activityTypeMedication: 'Medication',
        activityTypeMealSupport: 'Meal Support',
        activityTypeMobility: 'Mobility',
        activityTypeBehaviourSupport: 'Behaviour support',
        activityTypeClinical: 'Clinical',
        activityTypeCommunity: 'Community',
        inappropriateBehaviourFlags: 'Inappropriate Behaviour Flags',
        placeholderInappropriateBehaviourFlags: 'Select all applicable inappropriate behaviour flags',
        behaviourFlagOffensiveLanguage: 'Offensive language',
        behaviourFlagSexualisedComments: 'Sexualised comments',
        behaviourFlagVerbalAggression: 'Verbal aggression',
        behaviourFlagBoundaryCrossing: 'Boundary crossing',
        behaviourFlagPhysicalAggression: 'Physical aggression',
        behaviourFlagInappropriateTouch: 'Inappropriate touch',
        behaviourFlagWandering: 'Wandering / absconding',
        continenceCareNeeds: 'Continence Care Needs',
        placeholderContinenceCareNeeds: 'Select all applicable continence care needed',
        continenceNeedUrinaryIncontinence: 'Urinary incontinence requiring pull-ups/pads',
        continenceNeedContinentOfBowels: 'Continent of bowels',
        continenceNeedPartialAssistanceProducts:
          'Requires partial assistance with pulling up products',
        placeholderContinenceOutcomeGoal:
          'e.g., Sarah remains clean, dry, and comfortable; maintains skin integrity with no breakdown',
        placeholderContinenceAbleToDoThemselves:
          'e.g., Indicate need for toileting at times; participate in changing/pulling up products',
        careActionCommunicationApproach: 'Communication Approach',
        careActionManagingBehaviour: 'Managing Inappropriate Behaviour',
        careActionContinenceManagement: 'Continence Management',
        careActionHygieneSkinCare: 'Hygiene & Skin Care',
        careActionMedicationProtocols: 'Medication Administration Protocols',
        careActionMobilitySupport: 'Mobility Support',
        careActionNutritionSupport: 'Nutrition & Hydration Support',
        careActionPersonalCareSupport: 'Personal Care Support',
        careActionSkinIntegrityMonitoring: 'Skin Integrity Monitoring',
        careActionBehaviourSupport: 'Behaviour / Psychosocial Support',
        careActionRiskEscalation: 'Risk & Emergency Escalation Protocols',
        placeholderCareActionCommunication:
          'Use short, simple, clear instructions. Allow time for processing and response...',
        placeholderCareActionBehaviour:
          'Use neutral, non-confrontational responses. Set clear limits and maintain a safe distance...',
        placeholderCareActionContinence:
          'Ensure appropriate pull-ups/pads are worn at all times. Prompt and assist with regular toileting. Provide partial assistance with changing products.',
        placeholderCareActionHygiene:
          'Maintain good perineal hygiene after each episode. Check skin integrity during care for redness, irritation, and breakdown. Apply barrier cream if indicated.',
        placeholderCareActionMedication:
          'Administer as per medication chart/Webster pack. Follow the 6 rights of administration',
        requiredModuleComplete: 'Mark this module as complete to continue',
        requiredModulesIncomplete: 'Complete all enabled modules before continuing',
        requiredField: 'This field is required',
        careNeedsSummary: 'Care Needs Summary',
        outcomeGoal: 'Outcome/Goal',
        ableToDoThemselves: 'Able to do themselves',
        communicationNotesLabel: 'Communication Notes',
        medicationSupportLevel: 'Medication Support Level',
        allergiesAdverseReactions: 'Allergies / Adverse Reactions',
        diabetesMonitoringProtocol: 'Diabetes Monitoring Protocol',
        missedRefusedMedicationAction: 'Missed / Refused Medication Action',
        workersRequired: 'Number of Workers Required',
        workersRequiredHelper:
          'Ties directly into roster validation templates to prevent single-worker assignment errors',
        workersRequiredOne: '1 worker',
        workersRequiredTwo: '2 workers',
        workersRequiredThree: '3 workers',
        workersRequiredFourPlus: '4+ workers',
        mobilityBaselineEquipment: 'Mobility Baseline & Equipment',
        transferMethod: 'Transfer Method',
        careActionFallsPrevention: 'Falls Prevention',
        careActionHydrationMonitoring: 'Hydration Monitoring',
        careActionEarlyIntervention: 'Early Intervention & Prevention',
        careActionSafetyRiskMinimisation: 'Safety & Risk Minimisation',
        careActionPersonalHygieneRoutine: 'Personal Hygiene Routine',
        careActionSupportAssistanceGuidelines: 'Support & Assistance Guidelines',
        careActionSkinMonitoring: 'Skin Monitoring & Incontinence Care',
        careActionWoundPickingBehaviour: 'Managing Wound Picking Behaviour',
        careActionEarlyWarningSigns: 'Early Warning Signs',
        careActionImmediateContainment: 'Immediate Worker Containment Actions',
        careActionEscalationThreshold: 'Escalation Threshold & Contacts Grid',
        medicationSupportPrompting: 'Prompting only',
        medicationSupportSupervision: 'Supervision',
        medicationSupportFullAdministration: 'Full physical administration',
        medicationSupportSelfManagedWithPrompt: 'Self-managed with prompt',
        placeholderCareNeedsSummary:
          'Detail baseline care needs, risks, and support requirements...',
        placeholderOutcomeGoal: 'e.g., Reduce risk of falls and injury outdoors or when fatigued.',
        placeholderAbleToDoThemselves:
          'e.g., Able to mobilise independently within the home with prompting.',
        placeholderCommunicationCareNeedsSummary:
          'e.g., Sarah requires clear, consistent communication due to moderate intellectual disability...',
        placeholderCommunicationOutcomeGoal:
          'e.g., Sarah communicates her needs appropriately and safely; reduction in offensive or sexualised language',
        placeholderCommunicationAbleToDoThemselves:
          'e.g., Able to verbally communicate needs when calm; able to understand simple instructions',
        placeholderMedicationSupportLevel: 'Type or select assistance depth...',
        placeholderMedicationCareNeedsSummary:
          'Summarize the required supervision level (e.g., prompting, full physical administration) and explicitly document high-risk comorbidities (e.g., insulin-dependent Type 2 Diabetes, Bipolar Disorder) along with critical symptoms to monitor.',
        placeholderMedicationOutcomeGoal:
          'e.g., Medications are administered safely, correctly, and on time; prevent adverse events',
        placeholderMedicationAbleToDoThemselves:
          'e.g., Sarah is able to take oral medications when prompted. Unable to independently manage or understand risks',
        placeholderAllergiesAdverseReactions:
          '⚠️ LIST ALL CRITICAL MEDICAL, CHEMICAL, OR NUTRITIONAL ALLERGIES...',
        placeholderDiabetesMonitoringProtocol:
          'Monitor for signs of hypoglycaemia like sweating/shakiness, and hyperglycaemia like fatigue',
        placeholderMissedRefusedMedicationAction:
          'Detail exact immediate operational guidelines for the field worker if the client explicitly refuses their dose...',
        placeholderMobilityCareNeedsSummary:
          'Detail baseline physical mobility limits, motivation barriers, mechanical aid requirements, and specific fall hazards...',
        placeholderMobilityAbleToDoThemselves:
          'e.g., Able to mobilise independently within the home, walk short distances, and use stairs independently for one level.',
        placeholderWorkersRequired: 'Select Number of Workers',
        placeholderMobilityBaselineEquipment:
          'Detail baseline physical mobility and list required devices (e.g., Slide sheets, walking frame)...',
        placeholderTransferMethod:
          'Detail step-by-step instructions for transfers (e.g., Bed to wheelchair via ceiling hoist)...',
        placeholderFallsPrevention:
          'Ensure environment is clear of hazards. Supervise in unfamiliar environments and outdoor settings. Encourage appropriate footwear. Monitor safety when using stairs.',
        placeholderMobilitySupport:
          'Encourage regular mobilisation throughout the day. Prompt and support participation in walking. Avoid unnecessary use of wheelchair where safe.',
        placeholderNutritionSupport:
          'Support meal preparation and monitor intake. Follow IDDSI texture modifications as prescribed...',
        placeholderHydrationMonitoring:
          'Offer fluids regularly throughout the day. Monitor for signs of dehydration...',
        placeholderEarlyIntervention:
          'Identify early signs of escalation: Agitation, increased verbal behaviour, restlessness. Maintain routine and predictability. Minimise environmental stressors.',
        placeholderSafetyRiskMinimisation:
          'Ensure environment remains safe and supervised. Monitor for behaviours that may impact skin integrity, medication compliance, nutrition, and engagement.',
        placeholderBehaviourCareNeedsSummary:
          'Detail cognitive or impulse control deficits, mapped behaviors of concern, environmental triggers, and baseline care consistency requirements...',
        placeholderBehaviourOutcomeGoal:
          'e.g., Sarah demonstrates safer, more appropriate interactions, reducing the frequency and severity of behaviours of concern. Maintains emotional stability with support.',
        placeholderBehaviourAbleToDoThemselves:
          'e.g., Able to engage in activities when supported. Requires support to maintain behavioural control, understand boundaries, and use coping strategies.',
        placeholderPersonalHygieneRoutine:
          'Provide daily sponge bath. Full shower 3 times per week. Ensure all care is delivered using safe manual handling techniques and a shower commode chair for safety.',
        placeholderSupportAssistanceGuidelines:
          'Provide full assistance with washing, dressing, and grooming. Give simple, step-by-step instructions. Maintain privacy at all times. Respect preferences and comfort.',
        placeholderPersonalCareCareNeedsSummary:
          'Outline required level of hygiene support, specific equipment needed, skin integrity risks from care refusal, and dependency levels..',
        placeholderPersonalCareOutcomeGoal:
          'e.g., Personal care is completed in a safe, respectful, and consistent manner while reducing the risk of skin breakdown.',
        placeholderPersonalCareAbleToDoThemselves:
          'e.g., May assist with aspects of personal care when cooperative, follow simple instructions with prompting. Participation is highly inconsistent and dependent on mood.',
        placeholderSkinIntegrityCareNeedsSummary:
          'Specify medical drivers of breakdown (e.g., incontinence friction, slow diabetic tissue regeneration) and physical picking habits...',
        placeholderSkinIntegrityOutcomeGoal:
          'e.g., Skin remains intact, clean, and free from breakdown or infection. Any wounds are identified early, managed promptly, and wound picking behaviours are reduced.',
        placeholderSkinIntegrityAbleToDoThemselves:
          'e.g., May report discomfort or pain (not always reliably), or participate in personal care with prompting. Unable to independently manage skin care or wound care.',
        placeholderSkinMonitoring:
          'e.g., Perform daily skin checks particularly in perineal area (due to incontinence), pressure areas (sacrum, heels, buttocks), and any existing wounds. Change products promptly after soiling.',
        placeholderWoundPickingBehaviour:
          'e.g., Follow Behaviour Support Plan. Redirect behavior: Offer alternative activities, provide distraction, and keep nails short and clean. Monitor closely for increased picking or new wounds.',
        placeholderEarlyWarningSigns:
          'Identify early signs of escalation: Agitation, increased verbal behaviour, restlessness. Maintain routine and predictability. Minimise environmental stressors.',
        placeholderImmediateContainment:
          'Detail explicit, step-by-step physical and environmental safety actions to execute the moment early warning signs present (e.g., clear the space of sharp objects, step back to maintain a safe distance, use low, calm vocal directions)',
        placeholderEscalationThreshold:
          'Define exact thresholds for calling treating clinicians, Registered Nurses, family advocates, or 000 emergency services. Provide an ordered list of critical phone numbers.',
        placeholderRiskEscalationCareNeedsSummary:
          'Identify specific environmental or physical hazards endangering the participant (e.g., uncontrolled seizure activity, dangerous wandering/absconding tendencies, severe choking risks, or sudden medical distress).',
        placeholderRiskEscalationOutcomeGoal:
          'e.g., Core safety risks and physiological hazards are mitigated proactively. Crisis situations are managed smoothly to completely avoid preventable hospitalization, severe injury, or medication-related adverse incidents.',
        placeholderRiskEscalationAbleToDoThemselves:
          'e.g., Participant is able to express localized physical discomfort or frustration verbally but cannot reliably identify internal safety hazards, predict clinical risks, or manage emergency scenarios independently.',
        modulesIncompleteTitle: 'Modules incomplete',
        modulesIncompleteBody:
          'Fill every required field in each enabled module, then mark each module complete before opening Summary.',
        noModulesEnabledTitle: 'No modules selected',
        noModulesEnabledBody:
          'Return to Classification & Risk Triage and select at least one care need to build your module stack.',
        stepComingSoonTitle: 'Coming soon',
        stepComingSoonBody:
          'This step will be available in an upcoming release. Complete Setup and save your draft to preserve progress.',
        stepperLabel: 'Care plan wizard steps',
        planName: 'Plan Name',
        planType: 'Plan Type',
        planTypeNursing: 'Nursing Care Plan',
        planTypeWound: 'Wound Management',
        planTypeEnteral: 'Enteral Feed',
        planTypeBowel: 'Complex Bowel Care',
        planOwner: 'Plan Owner',
        serviceSite: 'Site / Location',
        effectiveFrom: 'Effective From',
        reviewDate: 'Review Date',
        planSummary: 'Plan Summary',
        planSummaryHelper:
          'Visible in the final output summary and published out to authorized field mobile screens for rapid worker briefing.',
        mobileVisibility: 'Mobile Visibility',
        mobileVisibilityHelper:
          'Controls whether the active plan summary is pushed live out to authorized support workers during shift delivery.',
        mobileVisibilityOn: 'Visible',
        mobileVisibilityOff: 'Hidden',
        requiredPlanName: 'Plan name is required',
        requiredPlanType: 'Plan type is required',
        requiredPlanOwner: 'Plan owner is required',
        requiredServiceSite: 'Site / location is required',
        requiredEffectiveFrom: 'Effective from date is required',
        requiredReviewDate: 'Review date is required',
        requiredPlanSummaryWhenMobileVisible:
          'Plan summary is required when mobile visibility is enabled',
        reviewDateAfterEffectiveFrom: 'Review date must be on or after effective from date',
        nextTriage: 'Next: Classification & Risk Triage',
        nextModules: 'Next: Module & Plan Stack',
        nextSummary: 'Next: Summary',
        nextCta: 'Next',
        backSetup: 'Back: Setup',
        backTriage: 'Back: Classification & Risk Triage',
        backModules: 'Back: Module & Plan Stack',
        backToCarePlanList: 'Back to Care Plan',
        viewOnlySummaryHint: 'This care plan is not a draft. You can view the summary only.',
        saveDraft: 'Save draft',
        discardDraft: 'Discard Draft',
        placeholderPlanName: 'e.g., Standard Daily Living Support',
        placeholderPlanType: 'Select care plan routing pathway...',
        planTypeWizardComingSoon:
          'This care plan type wizard is coming soon. You can save setup details, but only Nursing Care Plan and Wound Management Care Plan can proceed beyond this step.',
        placeholderPlanOwner: 'Search by internal staff name...',
        placeholderServiceSite: 'Select operational service hub / location...',
        placeholderEffectiveFrom: 'Select start date',
        placeholderReviewDate: 'Select scheduled review date',
        placeholderPlanSummary:
          "Provide a plain-language overview of the participant's core needs, primary support goals, and background details...",
        versionNo: 'Version No',
        careStartDate: 'Care Start Date',
        careObjectives: 'Care Objectives',
        medicationPlan: 'Medication Plan',
        mobilityRequirements: 'Mobility Requirements',
        behaviouralNotes: 'Behavioural Notes',
        dietaryRequirements: 'Dietary Requirements',
        minimumStaffLevel: 'Minimum Staff Level',
        extraCertifications: 'Extra Certifications',
        serviceFrequency: 'Service Frequency',
        visitDuration: 'Visit Duration',
        careLocation: 'Care Location',
        requiredCareStartDate: 'Care Start Date is required',
        reviewDateAfterCareStart: 'Review date must be on or after care start date',
        summarySectionPlanSummary: 'Plan Summary',
        summaryPlanOwnerLabel: 'Plan Owner',
        summaryServiceSiteLabel: 'Site / Location',
        summaryMobileVisibilityLabel: 'Mobile Visibility',
        activityName: 'Activity Name',
        description: 'Description',
        mandatory: 'Mandatory?',
        orderNumber: 'Order Number',
        addNewActivity: 'Add New Activity',
        removeActivity: 'Remove Activity',
        requiredAtLeastOneActivity: 'Add at least one activity to continue',
        requiredActivityName: 'Activity Name is required',
        requiredActivityType: 'Activity Type is required',
        requiredActivityDescription: 'Description is required',
        requiredActivityTimeStart: 'Start time is required',
        requiredActivityTimeEnd: 'End time is required',
        requiredMandatory: 'Mandatory selection is required',
        requiredOrderNumber: 'Order Number is required',
        summaryCardTitle: 'Care Plan Draft Summary',
        discardConfirmTitle: 'Discard Care Plan?',
        discardConfirmBody: (
          planDisplay: string,
          participantName: string,
        ) =>
          `Are you sure you want to delete ${planDisplay} for Participant: ${participantName}?`,
        discardConfirmHint: '⚠️ This will remove all your progress.',
        discardConfirmCancel: 'Cancel',
        discardConfirmConfirm: 'Discard Care Plan',
        draftBy: 'Drafted By',
        draftedAt: 'Drafted At',
        nextAction: 'Next Step',
        summaryParticipantLabel: 'Participant:',
        summaryMrnLabel: 'MRN:',
        summaryNdisLabel: 'NDIS:',
        summaryDobLabel: 'DOB:',
        summarySiteLabel: 'Site:',
        summarySectionGeneralAdministration: 'General Administration',
        summarySectionClinicalSafety: 'Clinical & Safety Profile',
        summarySectionRosterRules: 'Roster Automation Rules',
        summarySectionActivities: 'Mandatory and Optional Activities',
        summarySectionGovernance: 'Final Governance Action',
        summarySectionOperationalGovernance: 'Operational Governance & Roles',
        summaryGovernanceNameLabel: 'Name',
        summaryGovernanceRelationshipLabel: 'Relationship',
        summaryGovernancePrimaryCareOrg: 'Primary Care Organisation',
        summaryGovernanceLegalGuardian: 'Legal Guardian / Advocate',
        summaryRolesResponsibilities: 'Roles and Responsibilities',
        summaryToggleRolesResponsibilities: 'Toggle roles and responsibilities',
        summaryToggleActivityDescription: (activityName: string) =>
          `Toggle description for ${activityName}`,
        summaryStatusPendingGuardianConsent: '(Pending Guardian Consent)',
        summaryStatusSignedActive: '(Signed and Active)',
        summaryStatusArchived: '(Archived)',
        summaryStatusNoCarePlan: '(No Care Plan)',
        summaryStatusDraft: '(Draft)',
        summaryNextStepNotify: (guardianName: string) =>
          `Notify Participant and Legal Guardian (${guardianName}) for Digital Consent.`,
        triageSummaryDailyLiving: 'Daily Living Support',
        triageSummaryMedication: 'Medication Support',
        triageSummaryMobility: 'Mobility',
        triageSummaryContinence: 'Continence Management',
        triageSummaryCommunication: 'Communication Support',
        triageSummaryNutrition: 'Nutrition and Hydration',
        triageSummarySkinIntegrity: 'Skin Integrity',
        triageSummaryBehaviour: 'Behaviour / Psychosocial',
        triageSummaryPersonalCare: 'Personal Care',
        triageSummaryRiskEscalation: 'Risk and Emergency Escalation',
        sendForAcknowledgment: 'Send for Acknowledgment',
        summarySendingForAcknowledgment: 'Sending…',
        summaryPlanNameLabel: 'Plan Name',
        summaryStatusLabel: 'Status',
        summaryCareDatesLabel: 'Care Dates',
        summaryRiskAlertsLabel: 'Risk Alerts',
        summaryStaffingLevelLabel: 'Staffing Level',
        summaryRequiredCertificationsLabel: 'Required Certifications',
        summaryShiftDurationLabel: 'Shift Duration',
        summaryLocationLabel: 'Location',
        summaryOrderLabel: 'Order',
        summaryNextActionLabel: 'Notify guardian for digital consent.',
        yesOption: 'Yes',
        noOption: 'No',
        placeholderSelectFrequency: 'Select frequency',
        placeholderVisitDuration: 'e.g. 4.0',
        placeholderSelectCertifications: 'Select certifications',
        placeholderSelectStaffLevel: 'Select Staff Level',
        placeholderOrderNumber: 'Select activity order number',
        placeholderSelectYesNo: 'Select yes / no',
        placeholderCareLocation: 'e.g., Participant Residence & Local Park',
        placeholderCareObjectives:
          "Describe the participant's goals (e.g., 'Increase community participation by attending local bowling on Tuesdays').",
        placeholderMedicationPlan:
          "List dosages, timing, and administration instructions (e.g., '50mg Sertraline with breakfast').",
        placeholderMobility:
          'e.g., 1-person steadying assist; uses walking frame for distances >50m.',
        placeholderBehavioural:
          "e.g., Can become distressed by loud sirens; use 'Calm Voice' protocol.",
        placeholderDietary: 'e.g., Minced and Moist (Level 4 IDDSI); no honey.',
        placeholderVersionNo: 'e.g., 1',
        placeholderActivityName: 'e.g., Morning Medication',
        placeholderActivityDescription:
          'e.g., Administer 50mg Sertraline. Ensure participant has finished breakfast first.',
        placeholderCareStartDate: 'Select start date',
        staffLevelOptionSupportWorker1: 'Support Worker (Level 1)',
        staffLevelOptionSupportWorker2: 'Support Worker (Level 2)',
        staffLevelOptionRegisteredNurse: 'Registered Nurse (RN)',
        certificationOptionManualHandling: 'Manual Handling',
        certificationOptionMedicationAdministration: 'Medication Administration',
        certificationOptionEpilepsyMidazolam: 'Epilepsy & Midazolam',
        certificationOptionMealtimeManagement: 'Mealtime Management',
        certificationOptionFirstAidCpr: 'First Aid & CPR',
        certificationOptionBehaviorSupportBip: 'Behavior Support (BIP)',
        serviceFrequencyOptionDaily: 'Daily',
        serviceFrequencyOptionWeekly: 'Weekly',
        serviceFrequencyOptionFortnightly: 'Fortnightly',
        serviceFrequencyOptionMonthly: 'Monthly',
        serviceFrequencyOptionAdhocOncall: 'Ad-hoc / On-call',
        visitDurationOptionStandard: 'Standard (1.0 - 4.0 hrs)',
        visitDurationOptionExtended: 'Extended (6.0 - 8.0 hrs)',
        visitDurationOptionPassiveOvernight: 'Passive Overnight (8.0 - 10.0 hrs)',
        visitDurationOptionActiveOvernight: 'Active Overnight',
        sendForApproval: 'Send to Guardian for Approval',
        saveSuccessTitle: 'Draft saved',
        saveSuccessBody: 'Care plan draft has been saved.',
        saveErrorTitle: 'Could not save draft',
        discardSuccessTitle: 'Draft discarded',
        discardSuccessBody: 'Care plan draft has been discarded.',
        sendSuccessTitle: 'Awaiting Consent',
        sendSuccessBody: 'Care plan has been sent to the guardian and is now awaiting consent.',
        dispatchDialogTitle: 'Care Plan Draft Dispatched',
        dispatchDialogBody: (participantName: string, versionNo: number, guardianName: string) =>
          `The Care Plan for ${participantName} (v${versionNo}.0) has been successfully locked and sent to ${guardianName} (Guardian) for review.`,
        dispatchDialogNextStepsTitle: 'Next Steps:',
        dispatchDialogNextStep1: 'Guardian provides digital consent via the secure link.',
        dispatchDialogNextStep2: 'Admin performs final clinical sign-off.',
        dispatchDialogNextStep3:
          'System activates automated shift generation (Daily, 4-hour blocks).',
        dispatchDialogCta: 'Return to Participant Hub',
        guardianSignatureSectionTitle: 'Guardian Signature',
        signedAtLabel: 'Signed At',
        signatureLabel: 'Signature',
        guardianSignatureAlt: 'Guardian signature',
      },
      guardianInvite: {
        successTitle: 'Invitation Sent to Guardian',
        successBody: (guardianDisplay: string, email: string, participantName: string) =>
          `A secure portal invitation has been dispatched to ${guardianDisplay} (${email}). ${participantName}'s profile is now ready for Care Plan drafting.`,
        errorTitle: 'Invite failed',
        errorBody: 'Could not send guardian portal invite. Please try again.',
        anonymousGuardian: 'the guardian',
        sendButton: 'Send Portal Invite to Guardian',
        tooltipMissingEmail: 'Cannot send invite: guardian email is missing on profile.',
        tooltipInvalidEmail:
          'Guardian email on this profile is not valid. Update the profile and try again.',
      },
      archiveDialog: {
        title: (name: string) => `Archive Patient: ${name}`,
        description:
          'You are about to move this participant to the archives. This will have the following immediate effects:',
        rosterLabel: 'Roster: ',
        rosterBody: (name: string) => `All future shifts for ${name} will be cancelled.`,
        accessLabel: 'Access: ',
        accessBody: 'The Guardian Portal for this participant will be deactivated.',
        complianceLabel: 'Compliance: ',
        complianceBody: "Care Plans and Progress Notes will be moved to 'Read-Only' storage.",
        reasonLabel: 'Reason for Archiving',
        reasonPlaceholder: 'select or type reason',
        reasons: [
          'Patient Left Service',
          'Funding Ended',
          'Transitioned provider',
          'Deceased',
          'Data Entry Error',
        ],
        cancelCta: 'Cancel',
        confirmCta: 'Confirm & Archive',
        confirmBusyCta: 'Archiving…',
        successTitle: 'Profile Archived',
        successBody: (name: string) =>
          `${name} has been moved to the Archived Directory. All active automated scheduling has been paused.`,
        errorTitle: 'Could not archive patient',
        errorBody: 'Please try again in a moment.',
      },
      bulkArchiveDialog: {
        title: (count: number) => `Archive ${count} Selected Patients?`,
        description: (count: number) =>
          `You are about to archive ${count} participants. This action will immediately impact your operations for all selected records:`,
        schedulesLabel: 'Schedules: ',
        schedulesBody: 'All future shifts for these participants will be permanently cancelled.',
        accessLabel: 'Access: ',
        accessBody: 'Guardians will lose access to their respective portals.',
        dataLabel: 'Data: ',
        dataBody: "Records will be moved to 'Archived' status and will no longer appear in active lists.",
        reasonLabel: 'Reason for Archiving',
        reasonPlaceholder: 'select or type reason',
        safetyHeading: 'Required Action (for Safety)',
        safetyCheckbox: (count: number) =>
          `I understand that this will cancel all upcoming shifts for these ${count} participants.`,
        cancelCta: 'Cancel',
        confirmCta: 'Confirm & Archive',
        confirmBusyCta: 'Archiving…',
        ackRequiredError: 'Please acknowledge the required safety action.',
        successTitle: 'Bulk Archive Complete',
        successBody: (count: number) =>
          `${count} patients have been successfully moved to archives and their shifts have been removed.`,
        errorTitle: 'Could not archive selected patients',
        errorBody: 'Please try again in a moment.',
      },
      archivedPage: {
        metaTitle: 'Archived Patients',
        breadcrumbHub: 'Patients Hub',
        breadcrumbCurrent: 'Archived Patients',
        title: 'Archived Patients',
        subtitle:
          'View and manage inactive records. These participants are restricted from the active roster but their historical data and care notes remain available for audit purposes.',
        exportArchiveLog: 'Export Archive Log',
        sortBy: 'Sort By',
        archivedBy: 'Archived By',
        reasonForArchiving: 'Reason for Archiving',
        searchPlaceholder: 'Search Participant',
        permanentlyDeleteSelected: 'Permanently Delete selected?',
        restoreSelected: 'Restore Selected?',
        restoreSuccessTitle: 'Patient Record Restored',
        restoreSuccessBody: (name: string) =>
          `${name} is now active. Don't forget to review their Care Team and Roster to resume services.`,
        restoreBulkSuccessTitle: (count: number) => `${count} Patient Records Restored`,
        restoreBulkSuccessBody: 'The records have been moved to the active directory. You can now assign them to sites and teams.',
        restoreErrorTitle: 'Could not restore participant',
        restoreErrorBody: 'Please try again in a moment.',
        deleteSuccessTitle: 'Records Permanently Deleted',
        deleteSuccessBody: (count: number) =>
          `${count} records and all associated data have been purged from the system.`,
        deleteErrorTitle: 'Could not permanently delete records',
        deleteErrorBody: 'Please try again in a moment.',
        filesComingSoonTitle: 'Files',
        filesComingSoonBody: 'Archived files view is coming soon.',
        exportSuccessTitle: 'Archive log exported',
        exportSuccessBody: (count: number) =>
          `Downloaded CSV with ${count} archived patient record${count === 1 ? '' : 's'}.`,
        exportErrorTitle: 'Could not export archive log',
        exportErrorBody: 'Please try again in a moment.',
        loadError: 'Could not load archived participants. Try again.',
        emptyInitialTitle: 'No Archived Patients Yet',
        emptyInitialBody:
          "There are no archived participant records yet. Once participants are archived from Patients Hub, they'll appear here for audit and restoration workflows.",
        empty: 'No archived participants match your filters.',
        rowActions: 'Row actions',
        selectRow: 'Select row',
        columns: {
          nameMrn: 'Name & MRN',
          dateArchived: 'Date Archived',
          archivedBy: 'Archived By',
          reason: 'Reason for Archiving',
          actions: 'Actions',
        },
        restoreButton: 'Restore',
        viewFilesButton: 'View Files',
        restoreDialogTitleSingle: (name: string) => `Restore ${name}?`,
        restoreDialogTitleBulk: (count: number) => `Restore ${count} Patients?`,
        restoreDialogBodySingle:
          'Restoring this patient will reactivate their profile. Please note:',
        restoreDialogBodyBulk: (count: number) =>
          `You are about to reactivate ${count} records. This will move them from the Archive back to your Active Patients list.`,
        restoreDialogPoint1Single: 'Their Guardian Portal will be reactivated.',
        restoreDialogPoint2Single:
          'You will need to re-assign a Care Team and re-publish shifts to the roster.',
        restoreDialogPoint3Single:
          "The previous Care Plan will be marked as 'Expired' and will require a new signature.",
        restoreDialogPoint1Bulk: 'Portals: Guardian access will be restored immediately.',
        restoreDialogPoint2Bulk:
          'Rostering: You will need to manually re-assign Care Teams and schedule new shifts.',
        restoreDialogPoint3Bulk:
          'Compliance: Please check if their Care Plans are still valid upon restoration.',
        restoreDialogConfirm: 'Confirm & Restore',
        deleteDialogTitle: (count: number) => `Permanently delete ${count} Patient records?`,
        deleteDialogBody: (count: number) =>
          `This action is irreversible. You are about to permanently remove ${count} patients and all their associated history data from the Salaamah AI database.`,
        deleteDialogPoint1:
          'Data Loss: This includes all progress notes, incident reports, and signed consents.',
        deleteDialogPoint2:
          'Compliance Warning: Ensure you are not violating NDIS record-keeping audits by deleting these files.',
        deleteDialogConfirmLabel: "Type 'DELETE PERMANENTLY' to confirm",
        deleteDialogConfirmPlaceholder: 'DELETE PERMANENTLY',
        deleteDialogConfirm: (count: number) => `Permanently Delete ${count} Records`,
        deleteDialogValidation: 'Please type DELETE PERMANENTLY to confirm.',
      },
      carePlanReminder: {
        successTitle: 'Care Plan Reminder Sent',
        successBodySingle: (guardianName: string, patientName: string) =>
          `A secure notification and a fresh magic link have been sent to ${guardianName}. They can now review and sign the plan for ${patientName}.`,
        bulkSuccessTitle: 'Bulk Reminders Sent',
        successBodyBulk: (count: number) =>
          `Successfully sent care plan reminders to ${count} guardians. You can track their signature status in the Participant list.`,
        errorTitle: 'Could not send care plan reminder',
        errorBody: 'Please try again in a moment.',
        noEligibleTitle: 'No eligible patients selected',
        noEligibleBody: 'Select at least one patient with Awaiting Consent status and a valid guardian email.',
        tooltipStatus: 'Reminders can only be sent when care plan status is Awaiting Consent.',
        tooltipMissingEmail: 'Add a guardian email before sending a reminder.',
        tooltipInvalidEmail: 'Enter a valid guardian email before sending a reminder.',
      },
      archiveParticipantRecord: 'Archive Participant Record',
      rowMenu: {
        viewProfile: 'View Profile',
        draftCarePlan: 'Draft Care Plan',
        viewCarePlanDraft: 'View Care Plan Draft',
        sendCarePlanReminder: 'Send Care Plan Reminder',
      },
      participantCreatedModal: {
        title: 'Participant Profile Created',
        registeredBody: (fullName: string, mrn: string) =>
          `${fullName} is now registered in the system (MRN: ${mrn}).`,
        guardianNotificationHeading: 'Guardian Notification:',
        guardianNotificationBody: (guardianLine: string, emailLine: string) =>
          `An invitation email has been dispatched to ${guardianLine} (${emailLine}) to activate their secure portal and review future care plans.`,
        noEmailProvided: 'no email provided',
        viewProfile: 'View Profile',
        addNewParticipant: 'Add New Participant',
        draftCarePlan: 'Draft Care Plan',
      },
      profilePage: {
        anonymousParticipant: 'Participant',
        loadingMetaTitle: 'Loading patient profile…',
        loadErrorTitle: 'Could not load patient profile',
        loadErrorBody: 'Showing available details only. Please try again.',
        metaTitle: (participantName: string) => `${participantName} | Profile Overview`,
        pageHeading: (participantName: string) => `${participantName} | Profile Overview`,
        breadcrumbAriaLabel: 'Patient profile breadcrumb',
        mrnLabel: 'MRN:',
        ndisIdLabel: 'NDIS ID:',
        monitoringStatusLabel: 'Monitoring Status:',
        clinicalSectionTitle: 'Clinical Identity & Safety',
        editActiveLabel: 'Cancel editing patient details',
        editInactiveLabel: 'Edit patient details',
        fieldFullName: 'Full Name',
        fieldNdisNumber: 'NDIS Number',
        fieldDateOfBirth: 'Date of Birth',
        dobPlaceholder: 'dd/mm/yyyy',
        fieldPrimaryDiagnosis: 'Primary Diagnosis',
        fieldAllergies: 'Allergies',
        fieldRiskFlags: 'Risk Flags',
        fieldPrimarySite: 'Primary site',
        fieldPrimaryCareTeam: 'Primary care team',
        fieldAdditionalRosterSites: 'Additional roster sites',
        readOnlyAdditionalRosterSites: 'Additional roster sites',
        readOnlyDateOfBirth: 'Date of Birth',
        readOnlyPrimaryDiagnosis: 'Primary Diagnosis',
        readOnlyAllergies: 'Allergies',
        readOnlyRiskFlags: 'Risk Flags',
        readOnlyPrimarySite: 'Primary site',
        readOnlyPrimaryCareTeam: 'Primary care team',
        guardianSectionTitle: 'Guardian Information',
        fieldGuardian: 'Guardian',
        fieldGuardianEmail: 'Guardian Email Address',
        readOnlyGuardian: 'Guardian',
        readOnlyGuardianEmail: 'Guardian Email Address',
        carePlanSectionTitle: 'Care Plan',
        carePlanColumnPlanName: 'Plan Name',
        carePlanColumnPlanType: 'Plan Type',
        carePlanColumnStatus: 'Status',
        carePlanColumnEffectiveFrom: 'Effective From',
        carePlanColumnReviewDate: 'Review Date',
        carePlanEmpty: 'No care plans yet. Draft a new care plan to get started.',
        carePlansLoading: 'Loading care plans…',
        carePlanDraftNew: 'Draft New Care Plan',
        carePlanCreating: 'Creating…',
        carePlanDeleteDraft: 'Delete draft care plan',
        carePlanDeleteSuccessTitle: 'Draft discarded',
        carePlanDeleteSuccessBody: 'Care plan draft has been discarded.',
        carePlanDeleteErrorTitle: 'Could not delete draft',
        carePlanDeleteErrorBody: 'Please try again.',
        carePlanLoadErrorTitle: 'Could not load care plans',
        carePlanLoadErrorBody: 'Please refresh the page and try again.',
        profileOverviewTab: 'Profile Overview',
        carePlanTab: 'Care Plan',
        incidentNotesTab: 'Incident Notes',
        incidentNotesComingSoon: 'Incident notes for this participant will appear here.',
        carePlanActionStartDraft: 'Draft Care Plan',
        carePlanActionContinueDraft: 'Continue Draft Care Plan',
        carePlanActionViewSummary: 'View Care Plan Summary',
        cancelEdit: 'Cancel',
        saveChanges: 'Save Changes',
        saving: 'Saving…',
        monitoringPlaceholder: 'Select monitoring status',
        currentSiteFallback: 'Current site',
        updateSuccessTitle: 'Profile updated',
        updateSuccessBody: 'Patient details have been updated successfully.',
        updateErrorTitle: 'Update failed',
        updateErrorBody: 'Could not update patient profile. Please review details and try again.',
        limitedDataNotice: (id: string) =>
          `Showing limited data for patient ID ${id}. Open this screen from New Intake success popup for complete details.`,
      },
    },
  },
  onboarding: {
    metaTitle: 'Let’s get you setup',
    loading: 'Loading onboarding…',
    progressStatus: 'Progress Status',
    verifyBannerPrefix: 'Action Required:',
    verifyBannerBody: 'Please click the link in your email to securely activate your profile.',
    heading: 'Let’s get you setup',
    organisationIdentity: {
      stepTitle: 'Organisation Identity',
      description:
        'Enter your NDIS registration details to set up your secure workspace. This information is used for compliance and audit reporting.',
      legalEntityLabel: 'Legal Entity Name',
      legalEntityHelper: 'Must match your ABN registration.',
      legalEntityPlaceholder: 'e.g., Sunshine Care Services Pty Ltd',
      abnLabel: 'ABN (Australian Business Number)',
      abnPlaceholder: '11-digit number',
      ndisLabel: 'NDIS Provider Number',
      ndisPlaceholder: 'e.g., 4-XXXX-XXXX',
      sectorLabel: 'Primary Sector',
      sectorPlaceholder: 'Select your primary sector',
      sectorLoading: 'Loading sectors…',
      submit: 'Save and Continue',
      submitSavingTitle: 'Identity Saved',
      submitSavingBody: 'Your organisation identity details have been saved.',
      submitError: 'Could not save your organisation details. Try again in a moment.',
      submitErrorTitle: 'Could Not Save',
      loadError: 'Could not load your onboarding state. Try again in a moment.',
      loadErrorTitle: 'Could Not Load Onboarding',
    },
    serviceSites: {
      stepTitle: 'Register Your Service Sites',
      description:
        'Define your physical locations or regional hubs. You can add more sites later in Org Settings.',
      addNew: 'Add New Site',
      addNewAriaLabel: 'Start a new service site',
      listTitle: 'Your Service Sites',
      listEmpty:
        'No service sites added yet. Fill in the form below and click “Add New Site”.',
      siteNameLabel: 'Site Name',
      siteNamePlaceholder: 'e.g., North Sydney Respite House',
      siteAddressLabel: 'Site Address',
      siteAddressPlaceholder: 'Start typing address…',
      primaryServiceTypeLabel: 'Primary Service Type',
      primaryServiceTypePlaceholder: 'Select primary service type',
      primaryServiceTypeLoading: 'Loading service types…',
      departmentNameLabel: 'Department Name',
      departmentNamePlaceholder: 'e.g., Support Work, Clinical, Admin',
      timezoneLabel: 'Timezone',
      timezonePlaceholder: 'Select timezone',
      optionalSectionTitle: 'Optional details',
      optionalSectionDescription:
        'Phone, site manager, external ID, and clock-in geofence can be added now or later in Organisation settings.',
      sitePhoneLabel: 'Site phone',
      sitePhonePlaceholder: 'e.g. 02 9876 5432',
      siteManagerNameLabel: 'Site manager',
      siteManagerNamePlaceholder: 'Person responsible for this location',
      externalIdLabel: 'External ID',
      externalIdPlaceholder: 'e.g. facility licence or registration number',
      geofenceRadiusLabel: 'Clock-in geofence (metres)',
      geofenceRadiusPlaceholder: 'e.g. 150',
      geofenceRadiusHelper: 'Optional. Used for mobile clock-in validation at this site.',
      submitContinue: 'Save and Continue',
      submitAdd: 'Add Site',
      removeAriaLabel: 'Remove service site',
      removeConfirm: 'Remove this service site?',
      siteCreatedTitle: 'Site Added',
      siteCreatedBody: 'Your service site has been added.',
      siteUpdatedTitle: 'Site Updated',
      siteUpdatedBody: 'Your service site has been updated.',
      siteRemovedTitle: 'Site Removed',
      siteRemovedBody: 'Your service site has been removed.',
      advancedTitle: 'Service Sites Saved',
      advancedBody: 'Your service sites are saved. Next up: Your Team.',
      submitError: 'Could not save your service site. Try again in a moment.',
      submitErrorTitle: 'Could Not Save',
      removeError: 'Could not remove the site. Try again in a moment.',
      removeErrorTitle: 'Could Not Remove',
      advanceError: 'Could not continue. Add at least one service site and try again.',
      advanceErrorTitle: 'Could Not Continue',
    },
    yourTeam: {
      stepTitle: 'Your Team',
      description:
        'Invite the teammates who’ll help run your organisation. You can assign each person a system role and link them to a service site.',
      newTeamMember: 'New Team Member',
      newTeamMemberAriaLabel: 'Start a new team member invite',
      listTitle: 'Invited Team Members',
      listEmpty: 'No team members invited yet. Add someone below or skip for now.',
      nameLabel: 'Team Member Name',
      namePlaceholder: 'e.g., Sarah Khan',
      emailLabel: 'Team Member Email',
      emailPlaceholder: 'e.g., sarah@example.com',
      roleLabel: 'System Role',
      rolePlaceholder: 'Select a system role',
      roleLoading: 'Loading roles…',
      siteLabel: 'Assign to Site',
      sitePlaceholder: 'Select a site (optional)',
      siteNone: 'No site assigned',
      submitAdd: 'Add Team Member',
      submitContinue: 'Send Invites & Continue',
      skip: "Skip for now, I'll add them later",
      removeAriaLabel: 'Remove team invite',
      removeConfirm: 'Remove this team invite?',
      inviteAddedTitle: 'Invite Added',
      inviteAddedBody: 'We’ll send the invite once you continue.',
      inviteRemovedTitle: 'Invite Removed',
      inviteRemovedBody: 'This team member was removed.',
      advancedTitle: 'Team Invites Saved',
      advancedBody: 'Your invites are saved. Next up: First Intake.',
      skippedTitle: 'Skipped for now',
      skippedBody: 'We’ll remind you to invite your team later.',
      submitError: 'Could not save this team invite. Try again in a moment.',
      submitErrorTitle: 'Could Not Save',
      removeError: 'Could not remove the invite. Try again in a moment.',
      removeErrorTitle: 'Could Not Remove',
      advanceError: 'Could not continue. Add at least one team member or use Skip for now.',
      advanceErrorTitle: 'Could Not Continue',
    },
    firstIntake: {
      stepTitle: 'Your First Patient Intake',
      heading: "Let's get you setup",
      description:
        'Register your first participant to unlock automated care planning and scheduling.',
      identityHeading: 'Identity',
      fullNameLabel: 'Full Name',
      fullNamePlaceholder: 'e.g., Sarah Jenkins',
      ndisLabel: 'NDIS Number',
      ndisPlaceholder: '9-digit ID',
      dobLabel: 'Date of Birth',
      dobPlaceholder: 'MM/DD/YY',
      guardianHeading: 'The Guardian',
      guardianNameLabel: 'Guardian Name',
      guardianNamePlaceholder: 'e.g., Robert Jenkins',
      guardianEmailLabel: 'Guardian Email Address',
      guardianEmailHint:
        'The guardian will receive an automated link to sign off on the Care Plan once drafted.',
      guardianEmailPlaceholder: 'r.jenkins@email.com',
      safetyHeading: 'Immediate Safety',
      diagnosisLabel: 'Primary Diagnosis',
      diagnosisHint: 'This provides the high-level clinical context for coordinators.',
      diagnosisPlaceholder: 'e.g., Spinal Cord Injury',
      allergiesLabel: 'Allergies',
      allergiesHint:
        "Safety-critical information. Leave blank only if 'No Known Allergies'.",
      allergiesPlaceholder: 'e.g., Penicillin, Latex, Peanuts (Anaphylaxis)',
      riskFlagsLabel: 'Risk Flags',
      riskFlagsPlaceholder: 'Select risk flags',
      riskFlagsEmpty: 'No risk flags selected',
      primarySiteLabel: 'Primary site',
      primarySitePlaceholder: 'Select site',
      additionalSitesLabel: 'Additional roster sites',
      additionalSitesPlaceholder: 'Select additional sites (optional)',
      additionalSitesHint:
        'Participants can only be rostered at their primary site and any additional sites you select here.',
      activeMonitoringLabel: 'on Active Monitoring',
      submitFinish: 'Finish Onboarding',
      savedTitle: 'Participant Registered',
      savedBody: "You're all set — welcome to Salaamah.",
      submitError: 'Could not register the participant. Try again in a moment.',
      submitErrorTitle: 'Could Not Save',
    },
    complete: {
      heading: 'Success!',
      subheading: "You're all set and everything is ready to go.",
      description: 'Start exploring Salaamah to see it in action!',
      cta: 'Go to Dashboard',
      autoRedirect: (seconds: number) =>
        `Redirecting to your dashboard in ${seconds}s…`,
    },
    steps: {
      organisationIdentity: 'Organisation Identity',
      serviceSites: 'Service Sites',
      yourTeam: 'Your Team',
      firstIntake: 'First Intake',
    },
    validation: {
      legalEntityRequired: 'Legal entity name is required',
      legalEntityMax: 'Legal entity name must be 255 characters or fewer',
      abnRequired: 'ABN is required',
      abnFormat: 'ABN must be exactly 11 digits',
      ndisRequired: 'NDIS Provider Number is required',
      ndisFormat: 'Enter a valid NDIS Provider Number',
      sectorRequired: 'Choose a primary sector',
      siteNameRequired: 'Site name is required',
      siteNameMax: 'Site name must be 255 characters or fewer',
      siteAddressMax: 'Site address must be 500 characters or fewer',
      serviceTypeRequired: 'Choose a primary service type',
      siteTimezoneRequired: 'Choose a timezone',
      departmentMax: 'Department name must be 255 characters or fewer',
      sitePhoneMax: 'Site phone must be 32 characters or fewer',
      siteManagerMax: 'Site manager name must be 255 characters or fewer',
      siteExternalIdMax: 'External ID must be 64 characters or fewer',
      siteGeofenceRadiusInvalid: 'Geofence radius must be between 1 and 50000 metres',
      teamMemberNameRequired: 'Team member name is required',
      teamMemberNameMax: 'Team member name must be 255 characters or fewer',
      teamMemberEmailRequired: 'Team member email is required',
      teamMemberEmailInvalid: 'Enter a valid team member email',
      teamMemberEmailDuplicate: 'This email is already in your list',
      teamMemberRoleRequired: 'Choose a system role',
      participantFullNameRequired: 'Participant full name is required',
      participantFullNameMax: 'Participant full name must be 255 characters or fewer',
      participantNdisFormat: 'NDIS Number must be 9 digits',
      participantDobRequired: 'Date of birth is required',
      participantDobInvalid: 'Enter a valid date of birth',
      participantDobFuture: 'Date of birth cannot be in the future',
      participantGuardianEmailRequired: 'Guardian email is required',
      participantGuardianEmailInvalid: 'Enter a valid guardian email',
      participantDiagnosisRequired: 'Primary diagnosis is required',
      participantDiagnosisMax: 'Primary diagnosis must be 255 characters or fewer',
      participantAllergiesMax: 'Allergies must be 2000 characters or fewer',
    },
  },
} as const;

export type EnglishMessages = typeof en;
